import { ArrowPathIcon, ArrowRightCircleIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, PlayIcon, PlusIcon } from "@heroicons/react/20/solid"
import Panel from "./Panel"
import MainStore from "./MainStore"
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { PrimitiveTable } from "./PrimitiveTable"
import NewPrimitivePanel from "./NewPrimitivePanel"
import { HeroIcon } from "./HeroIcon"
import { PrimitiveCard } from "./PrimitiveCard"
import AIProcessButton from "./AIProcessButton"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import QuestionEditor from "./QuestionEditor"
import SummaryCard from "./SummaryCard"
import useDataEvent from "./CustomHook"
import HierarchyNavigator from "./HierarchyNavigator"
import CollectionUtils from "./CollectionHelper"
import TooggleButton from "./ToggleButton"
import PrimitiveConfig from "./PrimitiveConfig"
import SearchSet from "./SearchSet"
import { QueryPane } from "./QueryPane"
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "./@components/description-list"
import { CheckboxField, Checkbox as LegacyCheckbox } from "./@components/checkbox"
import { AdjustmentsVerticalIcon, BackwardIcon, Bars3Icon, CloudArrowDownIcon, PlayCircleIcon, StopCircleIcon, TrashIcon, ViewColumnsIcon } from "@heroicons/react/24/outline"
import { Table } from "./Table"
import { Label } from "./@components/fieldset"
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, NumberInput, ScrollShadow, Select, SelectItem, Slider, Switch } from "@heroui/react"
import { Checkbox } from "@heroui/react";
import InputWithSync from "./InputWithSync"
import { Icon } from "@iconify/react/dist/iconify.js"
import { DebouncedNumberInput } from "./@components/DebouncedNumberInput"
import { DebouncedInput } from "./@components/DebouncedInput"
import { themes, tagColors } from "./RenderHelpers"
import { ColorSelector } from "./@components/ColorSelector.jsx"
import { DebouncedSlider } from "./@components/DebouncedSlider.jsx"
import ExternalPrimitiveConfigurator from "./integrations/ExternalPrimitiveConfigurator.jsx"
import { prepareStructuredSummary, resolveSummaryStructureTemplate } from "./utils/structuredSummary"
import { resolveFlowInstanceSelection } from "./utils/viewRendering"
import ActionFieldsEditor from "./ActionFieldsEditor"
import { getAllExtensions, toggleExtension } from "./extensions"

// Add the icons to the library


function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function formatPrimitiveProgress(progress, fallbackPercentage) {
    if (progress === null || progress === undefined) {
        if (typeof fallbackPercentage === 'number' && Number.isFinite(fallbackPercentage)) {
            const value = fallbackPercentage > 1 ? fallbackPercentage : fallbackPercentage * 100
            return `${Math.round(value)}% complete`
        }
        return null
    }

    if (typeof progress === 'string') {
        const trimmed = progress.trim()
        return trimmed.length > 0 ? trimmed : null
    }

    if (typeof progress === 'number' && Number.isFinite(progress)) {
        const value = progress > 1 ? progress : progress * 100
        return `${Math.round(value)}% complete`
    }

    if (typeof progress === 'object') {
        if (typeof progress.message === 'string') {
            const trimmed = progress.message.trim()
            if (trimmed.length > 0) {
                return trimmed
            }
        }

        if (typeof progress.percentage === 'number' && Number.isFinite(progress.percentage)) {
            const value = progress.percentage > 1 ? progress.percentage : progress.percentage * 100
            return `${Math.round(value)}% complete`
        }
    }

    return null
}

function safeStringify(value) {
    if (value === undefined) {
        return "undefined"
    }
    try {
        return JSON.stringify(value)
    } catch (error) {
        return String(value)
    }
}

function buildItemsKey(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return "0:0"
    }
    let hash = 0
    for (const item of items) {
        const id = item?.id ?? ""
        for (let i = 0; i < id.length; i++) {
            hash = (hash * 31 + id.charCodeAt(i)) >>> 0
        }
    }
    return `${items.length}:${hash}`
}

function parseTimelineEventTs(rawValue) {
    if (!rawValue) {
        return null
    }
    if (rawValue instanceof Date) {
        const time = rawValue.getTime()
        return Number.isFinite(time) ? time : null
    }
    if (typeof rawValue === "number") {
        return Number.isFinite(rawValue) ? rawValue : null
    }
    if (typeof rawValue !== "string") {
        return null
    }
    const trimmed = rawValue.trim()
    if (!trimmed) {
        return null
    }
    const parsed = Date.parse(trimmed)
    if (!Number.isNaN(parsed)) {
        return parsed
    }
    const yearOnly = trimmed.match(/^(\d{4})$/)
    if (yearOnly) {
        return Date.UTC(Number.parseInt(yearOnly[1], 10), 0, 1)
    }
    const yearMonth = trimmed.match(/^(\d{4})-(\d{2})$/)
    if (yearMonth) {
        return Date.UTC(
            Number.parseInt(yearMonth[1], 10),
            Math.max(0, Number.parseInt(yearMonth[2], 10) - 1),
            1
        )
    }
    return null
}

function normalizeTimelineEventKeyPart(value) {
    return String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .slice(0, 240)
}

function buildTimelineEventKey(node = {}) {
    const datePart = normalizeTimelineEventKeyPart(node.date ?? node.timestamp ?? node.time)
    const eventPart = normalizeTimelineEventKeyPart(node.event ?? node.name ?? node.title)
    const detailPart = normalizeTimelineEventKeyPart(node.overview ?? node.rationale ?? node.deal ?? node.source)
    return [datePart, eventPart, detailPart].join("|")
}

function collectTimelineEventsFromStructuredSummary(structuredSummary, summaryId) {
    const events = []
    const walk = (node) => {
        if (!node) {
            return
        }
        if (Array.isArray(node)) {
            node.forEach(walk)
            return
        }
        if (typeof node !== "object") {
            return
        }
        const ts = parseTimelineEventTs(node.date ?? node.timestamp ?? node.time)
        const eventText = [node.event, node.name, node.title].find(
            entry => typeof entry === "string" && entry.trim().length > 0
        )
        if (ts !== null && eventText) {
            const detail = [node.overview, node.rationale, node.deal, node.source]
                .find(entry => typeof entry === "string" && entry.trim().length > 0) ?? ""
            events.push({
                key: buildTimelineEventKey(node),
                date: node.date ?? "",
                event: eventText.trim(),
                detail: String(detail).trim(),
                ts,
                summaryId
            })
        }
        Object.values(node).forEach((value) => {
            if (value && typeof value === "object") {
                walk(value)
            }
        })
    }
    walk(structuredSummary)
    return events
}

function ensureViewConfigWithKey(config, idx) {
    if (!config) {
        return config
    }
    const proposed = config.viewKey ?? CollectionUtils.viewConfigKey({ ...config, id: config.id ?? idx })
    const key = proposed ?? `${idx}`
    if (config.viewKey === key) {
        return config
    }
    return {
        ...config,
        viewKey: key
    }
}

function sanitizeFlowConfigToken(value, fallback = "config") {
    const raw = `${value ?? ""}`.trim().toLowerCase()
    const normalized = raw
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
    return normalized || fallback
}

function makeUniqueFlowConfigToken(existingSet, baseToken) {
    const base = sanitizeFlowConfigToken(baseToken)
    let next = base
    let suffix = 2
    while (existingSet.has(next)) {
        next = `${base}_${suffix}`
        suffix += 1
    }
    return next
}

function cloneFlowConfigurations(configurations) {
    return Object.fromEntries(
        Object.entries(configurations ?? {}).map(([key, info]) => [
            key,
            {
                ...(info ?? {}),
                options: Array.isArray(info?.options)
                    ? info.options.map((option) => ({ ...(option ?? {}) }))
                    : []
            }
        ])
    )
}

const FLOW_PUBLISH_PARENT_RELATION_EXCLUSIONS = new Set(["primitives.imports", "primitives.validflow"])

function hasDirectPublishBlockingParentFlow(primitive) {
    return Object.entries(primitive?._parentPrimitives ?? {}).some(([parentId, paths]) => {
        if (!Array.isArray(paths) || paths.every((path) => FLOW_PUBLISH_PARENT_RELATION_EXCLUSIONS.has(path))) {
            return false
        }
        return mainstore.primitive(parentId)?.type === "flow"
    })
}

let mainstore = MainStore()
const tabs = [
    { name: 'Discovery', referenceId: 112, discovery: true, search: true },
    { name: 'Process', referenceId: 112, process: true },
    { name: 'Items', list: true }
]

const themeOptions = Object.keys(themes).map(key => ({
    key,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
}));

const mainTabs = [
    { name: 'Query', referenceId: 81, initial: true },
    { name: 'Summarize', referenceId: 113 },
    { name: 'One-shot', referenceId: 148, initial: true },
    { name: 'Compare', referenceId: 114 }
]

const defaultItemTableConfig = {
    fields: [
        { field: 'id', title: "ID", width: 80 },
        { field: 'title', title: "Title" }
    ]
}

function titleizeFieldKey(key) {
    return String(key ?? "")
        .replace(/[._]+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

function CategoryHeader({ itemCategory, items, newItemParent, actionAnchor, ...props }) {
    const [page, setPage] = useState(0)
    const [pageItems, setPageItems] = useState(50)
    const [activeTab, setActiveTab] = useState()

    const count = items.length
    const countLabel = count === 1 ? "1 item" : `${count} items`

    const cardConfig = defaultItemTableConfig

    const nestedActions = (itemCategory.actions?.filter(d => d.collectionAction || d.showInCollection)) ?? []
    const searchCategoryList = itemCategory.resultCategories?.map(d => d.searchCategoryIds ? d.searchCategoryIds.map(d2 => ({ id: d.id, title: d.title, searchCategoryIds: [d2] })) : undefined).flat().filter(d => d)
    const pageCount = Math.ceil(items.length / pageItems) - 1

    const referenceId = itemCategory?.id
    const configPrimitive = actionAnchor?.configParent ?? actionAnchor
    const baseTableConfig = props.panelConfig?.columns ?? items[0]?.metadata?.renderConfig?.table ?? cardConfig
    const isDynamicColumnsMode = Boolean(baseTableConfig?.columns)

    const availableFields = useMemo(() => {
        const byField = new Map()
        const seedConfig = items[0]?.metadata?.renderConfig?.table ?? cardConfig
        const seedFields = Array.isArray(seedConfig?.fields) ? seedConfig.fields : []
        seedFields.forEach((f) => {
            if (f && f.field != null && !byField.has(f.field)) {
                byField.set(f.field, { ...f })
            }
        })
        ;[{ field: 'id', title: "ID", width: 80 }, { field: 'title', title: "Title" }].forEach((f) => {
            if (!byField.has(f.field)) {
                byField.set(f.field, f)
            }
        })
        const discovered = []
        const fieldTitleOverrides = {
            structured_summary: "Summary content"
        }
        const sample = (items ?? []).slice(0, 6)
        sample.forEach((item) => {
            const params = item?.referenceParameters ?? {}
            Object.keys(params).forEach((key) => {
                if (!byField.has(key) && !discovered.find((d) => d.field === key)) {
                    discovered.push({ field: key, title: fieldTitleOverrides[key] ?? titleizeFieldKey(key) })
                }
            })
            const metaParams = item?.metadata?.parameters ?? {}
            Object.keys(metaParams).forEach((key) => {
                if (!byField.has(key) && !discovered.find((d) => d.field === key)) {
                    const param = metaParams[key]
                    discovered.push({ field: key, title: param?.title ?? titleizeFieldKey(key), type: param?.type })
                }
            })
        })
        discovered.sort((a, b) => String(a.title).localeCompare(String(b.title)))
        const hasSummaryWithSource = sample.some((item) => {
            if (item?.type !== "summary") {
                return false
            }
            let sources
            try {
                sources = item?.primitives?.source?.allItems
            } catch (err) {
                sources = undefined
            }
            return Array.isArray(sources) && sources.length > 0
        })
        if (hasSummaryWithSource && !byField.has("sourceTitle") && !discovered.find((d) => d.field === "sourceTitle")) {
            discovered.push({ field: "sourceTitle", title: "Source title" })
        }
        return [...Array.from(byField.values()), ...discovered]
    }, [items, referenceId])

    const storedFields = configPrimitive?.uiConfig?.[referenceId]?.config?.fields
    const [selectedFields, setSelectedFields] = useState(() => Array.isArray(storedFields) ? storedFields : null)
    useEffect(() => {
        const sf = configPrimitive?.uiConfig?.[referenceId]?.config?.fields
        setSelectedFields(Array.isArray(sf) ? sf : null)
    }, [configPrimitive?.id, referenceId])

    const persistTableFields = useCallback((fields) => {
        const target = configPrimitive
        if (!target?.setField) {
            return
        }
        const current = target.uiConfig ?? {}
        const refConfig = current[referenceId] ?? {}
        const nextUiConfig = {
            ...current,
            [referenceId]: {
                ...refConfig,
                config: { ...(refConfig.config ?? {}), fields }
            }
        }
        target.setField("uiConfig", nextUiConfig)
    }, [configPrimitive, referenceId])

    const handleColumnSelectionChange = useCallback((selection) => {
        const selectedSet = selection instanceof Set ? selection : new Set(selection)
        if (selectedSet.size === 0) {
            return
        }
        const next = availableFields.filter((f) => selectedSet.has(`${f.field}`))
        setSelectedFields(next)
        persistTableFields(next)
    }, [availableFields, persistTableFields])

    const defaultFields = useMemo(() => {
        const base = baseTableConfig?.fields
        if (Array.isArray(base) && base.length > 0) {
            return base
        }
        return availableFields.filter((f) => f.field === 'id' || f.field === 'title')
    }, [availableFields, baseTableConfig])

    const activeFields = (Array.isArray(selectedFields) && selectedFields.length > 0) ? selectedFields : defaultFields
    const effectiveTableConfig = (!isDynamicColumnsMode && Array.isArray(activeFields) && activeFields.length > 0)
        ? { ...baseTableConfig, fields: activeFields }
        : baseTableConfig

    return (
        <Panel
            collapsable
            noSpace
            title={itemCategory?.plural ?? `${itemCategory?.title}s`}
            count={countLabel}
            icon={<HeroIcon icon={itemCategory?.icon} className='w-5 h-5' />}
        >
            <div className="mt-3 mb-1.5">
                <nav aria-label="Tabs" className="flex space-x-4">
                    {tabs.map((tab) => (
                        <a
                            key={tab.name}
                            onClick={() => setActiveTab(tab)}
                            aria-current={activeTab?.name === tab.name ? 'page' : undefined}
                            className={classNames(
                                activeTab?.name === tab.name ? 'bg-ccgreen-200 text-ccgreen-900' : 'bg-gray-100 text-gray-500 hover:text-gray-700',
                                'rounded-md px-3 py-2 text-xs font-medium',
                            )}
                        >
                            {tab.name}
                        </a>
                    ))}
                </nav>
            </div>
            {activeTab?.referenceId && activeTab?.process && <div className="w-full flex flex-col mt-2">
                <NewPrimitivePanel key={activeTab.referenceId} newPrimitiveCallback={props.newPrimitiveCallback} parent={newItemParent} primitiveList={items} selectedCategory={mainstore.category(activeTab.referenceId)} />
            </div>}
            {activeTab?.list && <div className="w-full border bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 rounded-lg mt-2">
                <div className="flex p-2 space-x-1 text-gray-600">
                    {pageCount > 1 && <>
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => page > 0 ? setPage(page - 1) : undefined}
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </Button>
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => page < pageCount ? setPage(page + 1) : undefined}
                        >
                            <ChevronRightIcon className="w-5 h-5" />
                        </Button>
                    </>}
                    <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={props.createNewView ? () => props.createNewView(38, actionAnchor.id, props.filters, { referenceId: itemCategory.id, pivot: props.pivotRelationship, descend: props.pivotRelationship ? false : true }) : undefined}
                    >
                        <HeroIcon icon='FAAddView' className='w-5 h-5' />
                    </Button>
                    {!isDynamicColumnsMode && (
                        <Dropdown placement="bottom-end">
                            <DropdownTrigger>
                                <Button isIconOnly size="sm" variant="light" title="Configure columns">
                                    <ViewColumnsIcon className="w-5 h-5" />
                                </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                                aria-label="Table columns"
                                selectionMode="multiple"
                                closeOnSelect={false}
                                disallowEmptySelection
                                selectedKeys={new Set(activeFields.map((f) => `${f.field}`))}
                                onSelectionChange={handleColumnSelectionChange}
                            >
                                {availableFields.map((f) => (
                                    <DropdownItem key={`${f.field}`}>{f.title ?? f.field}</DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    )}
                    <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onPress={() => {
                            mainstore.promptDelete({
                                prompt: `Delete ${items.length} items?`,
                                handleDelete: async () => {
                                    for (const d of items) {
                                        await mainstore.removePrimitive(d)
                                    }
                                    return true
                                }
                            })
                        }}
                    >
                        <TrashIcon className='w-5 h-5' />
                    </Button>
                </div>
                <div className="relative">
                    <PrimitiveTable
                        primitive={actionAnchor}
                        page={page}
                        pageItems={pageItems}
                        onEnter={(d) => mainstore.sidebarSelect(d)}
                        config={effectiveTableConfig}
                        primitives={items}
                        className='w-full min-h-[24em] max-h-[60vh] !text-xs' />
                </div>
            </div>}
            {activeTab?.discovery && nestedActions.length > 0 &&
                <><PrimitiveCard.CardMenu
                    title="Actions"
                    buttonProps={{ variant: 'bordered', size: 'sm' }}
                    custom={nestedActions.map(d => {
                        const doAction = async (options) => {
                            await MainStore().doPrimitiveAction(
                                d.collectionAction ? items[0] : actionAnchor,
                                d.collectionAction ? d.key : "auto_cascade",
                                { cascade_key: d.collectionAction ? undefined : d.key, ids: items?.map(d => d.id), ...options },
                                () => { }
                            )
                        }
                        return {
                            ...d,
                            action: async () => {
                                if (d.actionFields) {
                                    mainstore.globalInputPopup({
                                        primitive: newItemParent,
                                        fields: d.actionFields,
                                        confirm: async (inputs) => await doAction(inputs)
                                    })
                                } else {
                                    await doAction()
                                }

                            }
                        }
                    })}
                />
                    {!props.filters && searchCategoryList && <div className="text-sm my-3 space-y-3">
                        {searchCategoryList.map(d =>
                            <Panel collapsable noSpace title={d.title}>
                                <SearchSet primitive={actionAnchor} resultSet={d.id} searchCategoryIds={d.searchCategoryIds} />
                            </Panel>
                        )}
                    </div>}
                </>

            }
        </Panel>
    )

}

export default function CollectionInfoPane({ board, frame, underlying, primitive, filters, localItems, ...props }) {
    const [activeTab, setActiveTab] = useState(mainstore.category(tabs.find(d => d.initial)))
    const [showNested, setShowNested] = useState(false)
    const [hideNull, setHideNull] = useState(frame?.referenceParameters?.explore?.hideNull)
    const [activeView, setActiveView] = useState(frame?.referenceParameters?.explore?.view ?? 0)
    const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)
    const [isPivotsPanelOpen, setIsPivotsPanelOpen] = useState(false)
    const [isAxisPanelOpen, setIsAxisPanelOpen] = useState(false)
    const [dataVersion, bumpDataVersion] = useReducer((count) => count + 1, 0)
    const filterPaneCacheRef = useRef({ key: null, panels: null })
    const ancestorsCacheRef = useRef({ key: null, value: [[], []] })
    const clearFilterPaneCache = useCallback(() => {
        filterPaneCacheRef.current = { key: null, panels: null }
    }, [])
    const clearAncestorsCache = useCallback(() => {
        ancestorsCacheRef.current = { key: null, value: [[], []] }
    }, [])
    const isTopLevelFlow = useMemo(() => {
        if (frame?.type !== "flow") {
            return false
        }
        return !hasDirectPublishBlockingParentFlow(frame)
    }, [frame?.id, frame?.type, dataVersion])


    useDataEvent("relationship_update set_parameter set_field delete_primitive", [board?.id, frame?.id, primitive?.id, underlying?.id].filter(d => d), (ids, event, info)=>{
        if( event === "set_field" && info?.startsWith("renderConfig") && !info?.startsWith("renderConfig._documentPage")){
            return false
        }
        bumpDataVersion()
        return false
    })

    const primitiveForContent = underlying ?? frame
    const primitiveContentId = primitiveForContent?.id ?? frame?.id ?? "no-primitive"
    const filtersSignature = safeStringify(filters)
    const localItemsSignature = buildItemsKey(localItems)
    const shouldComputeCollectionList = Boolean(frame && !["actionrunner", "search", "external"].includes(frame.type))
    const itemsData = useMemo(() => {
        if (!shouldComputeCollectionList || !primitiveForContent) {
            return { list: [], fullList: [] }
        }
        let computedList
        if (localItems !== undefined && localItems !== null) {
            computedList = localItems
        } else if (filters && typeof primitiveForContent.itemsForProcessingWithFilter === "function") {
            computedList = primitiveForContent.itemsForProcessingWithFilter(filters) ?? []
        } else {
            computedList = primitiveForContent.itemsForProcessing ?? []
        }
        let expandedList = typeof primitiveForContent.itemsForProcessingWithFilter === "function"
            ? primitiveForContent.itemsForProcessingWithFilter(filters, { ignoreFinalViewFilter: true })
            : computedList
        if (!expandedList) {
            expandedList = computedList
        }
        return {
            list: computedList ?? [],
            fullList: expandedList ?? []
        }
    }, [filters, filtersSignature, localItemsSignature, primitiveContentId, primitiveForContent, shouldComputeCollectionList, dataVersion, localItems])
    const list = shouldComputeCollectionList ? (itemsData.list ?? []) : []
    const filterableList = shouldComputeCollectionList ? (itemsData.fullList ?? itemsData.list ?? []) : []
    const listKey = buildItemsKey(list)
    const filterableListKey = buildItemsKey(filterableList)
    const timelineSummaryCategoryId = PrimitiveConfig.Constants.TIMELINE_EVENT_SUMMARY ?? 178
    const axisSourcePrimitive = props?.axisSource?.configParent ?? props?.axisSource
    const datasetOwner = (frame?.getConfig?.view_axis && axisSourcePrimitive)
        ? axisSourcePrimitive
        : (frame?.configParent ?? frame)
    const viewConfigOwner = frame ?? null
    const datasetViewConfig = datasetOwner?.referenceParameters?.datasetView ?? {}
    const datasetViewEnabled = Boolean(datasetViewConfig?.enabled)
    const datasetSourcePrimitive = datasetOwner ?? null
    const datasetSourceInstance = useMemo(()=>{
        if( !datasetSourcePrimitive?.flowElement ){
            return null
        }
        const instanceId = datasetViewConfig?.sourceInstanceId
        const instances = datasetSourcePrimitive?.primitives?.config?.allItems ?? []
        if( instanceId ){
            return instances.find(entry=>entry?.id === instanceId) ?? null
        }
        const underlyingInstance = underlying?.findParentPrimitives?.({type: "flowinstance"})?.[0]
        if( underlyingInstance && instances.some(entry=>entry?.id === underlyingInstance.id) ){
            return underlyingInstance
        }
        return instances.find(entry=>Array.isArray(entry?.itemsForProcessing) && entry.itemsForProcessing.length > 0) ?? null
    }, [datasetSourcePrimitive?.id, datasetViewConfig?.sourceInstanceId, underlying?.id, dataVersion])
    const datasetCandidates = useMemo(()=>{
        const source = datasetSourcePrimitive
        if( !source ){
            return {datasets: [], timelines: []}
        }
        const dataSource = underlying ?? datasetSourceInstance ?? source
        const dataPrimitives = Array.isArray(dataSource?.itemsForProcessing) && dataSource.itemsForProcessing.length > 0
            ? dataSource.itemsForProcessing.filter(Boolean)
            : [dataSource].filter(Boolean)
        const paramSets = dataPrimitives.map(entry=>entry?.referenceParameters ?? {})
        return CollectionUtils.datasetViewCandidatesFromParams(paramSets)
    }, [datasetSourcePrimitive?.id, datasetSourceInstance?.id, underlying?.id, dataVersion])

    let newPrimitiveCallback = props.newPrimitiveCallback


    const viewConfigs = useMemo(() => {
        if (!frame) {
            return []
        }
        if (viewConfigOwner?.type === "flow") {
            return (props.flowInstances ?? []).map((flowInstance, idx) => {
                const titleSuffix = flowInstance?.title ? ` - ${flowInstance.title}` : ""
                const baseConfig = {
                    id: flowInstance?.id ?? idx,
                    title: `Flow instance #${flowInstance?.plainId ?? idx + 1}${titleSuffix}`,
                    flowInstanceId: flowInstance?.id
                }
                return ensureViewConfigWithKey(baseConfig, idx)
            })
        }
        const metadataSource = list?.[0]?.metadata
        const baseConfigs = CollectionUtils.viewConfigs(metadataSource) ?? []
        const prepared = baseConfigs.map((cfg, idx) => ensureViewConfigWithKey(cfg, idx))
        if (datasetViewEnabled) {
            const datasetConfigs = CollectionUtils.datasetViewConfigs(metadataSource) ?? []
            return datasetConfigs.map((cfg, idx) => ensureViewConfigWithKey(cfg, idx))
        }
        return prepared
    }, [frame, frame?.type, list, props.flowInstances, datasetViewEnabled, datasetOwner?.id])

    const viewModeOptions = useMemo(() => {
        return viewConfigs.map((cfg, idx) => ({
            ...cfg,
            id: cfg.viewKey ?? `${idx}`
        }))
    }, [viewConfigs])

    const resolvedViewSelection = useMemo(
        () => CollectionUtils.matchViewConfig(viewConfigs, activeView),
        [viewConfigs, activeView]
    )
    const activeViewConfigId = resolvedViewSelection?.config?.id ?? resolvedViewSelection?.config?.viewKey
    const frameReferenceIds = [frame?.referenceParameters?.referenceId].flat().filter(Boolean)
    const isTimelineEventView = Boolean(
        frame?.type === "view" && (
            activeViewConfigId === "timeline_178_view" ||
            (resolvedViewSelection?.config?.title === "Event timeline" && frameReferenceIds.includes(timelineSummaryCategoryId))
        )
    )
    const timelineSourceItems = useMemo(() => {
        if (!isTimelineEventView) {
            return []
        }
        const combined = [
            ...(list ?? []),
            ...(filterableList ?? []),
            ...(props.originalList ?? [])
        ].filter(Boolean)
        const seen = new Set()
        return combined.filter((item) => {
            if (!item?.id) {
                return false
            }
            if (seen.has(item.id)) {
                return false
            }
            seen.add(item.id)
            return true
        })
    }, [isTimelineEventView, listKey, filterableListKey, props.originalList])
    const timelineEventsForControls = useMemo(() => {
        if (!isTimelineEventView) {
            return []
        }
        const summaryItems = (timelineSourceItems ?? []).filter(
            item => item?.type === "summary" && (
                item?.referenceId === timelineSummaryCategoryId ||
                Array.isArray(item?.referenceParameters?.structured_summary)
            )
        )
        if (summaryItems.length === 0) {
            return []
        }
        const deduped = new Map()
        summaryItems.forEach((summaryPrimitive) => {
            const events = collectTimelineEventsFromStructuredSummary(
                summaryPrimitive?.referenceParameters?.structured_summary,
                summaryPrimitive?.id
            )
            events.forEach((eventEntry) => {
                if (!eventEntry?.key) {
                    return
                }
                const existing = deduped.get(eventEntry.key)
                if (!existing || (Number.isFinite(eventEntry.ts) && eventEntry.ts < existing.ts)) {
                    deduped.set(eventEntry.key, eventEntry)
                }
            })
        })
        return Array.from(deduped.values()).sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0))
    }, [isTimelineEventView, timelineSourceItems, timelineSummaryCategoryId])

    const docDslPaginationInfo = useMemo(() => {
        if (frame?.type !== "element") {
            return null
        }
        const pageForflowInstance = props.parentPageInstance
        const pageMain = frame.origin
        if (!pageForflowInstance || !pageMain) {
            return null
        }

        const outputMap = Object.entries(pageMain.primitives.outputs ?? {})
        const entry = outputMap.find(d=>d[1] === frame.id || (Array.isArray(d[1]) && d[1].includes(frame.id) ))
        if( !entry ){
            return null
        }
        const inputs = pageForflowInstance.inputs ?? []
        const selectedInput = inputs[entry[0]?.split("_")?.[0]]
        if( !selectedInput ){
            return null
        }
        const instanceItems = selectedInput.source ?? []

        const sampleSummary = instanceItems.find(
            item => item?.type === "summary" && item?.referenceId === PrimitiveConfig.Constants.DOC_DSL_SUMMARY
        )
        if (!sampleSummary) {
            return null
        }
        const dsl = sampleSummary.getConfig?.prompt ?? {}
        if (!dsl || dsl.kind !== "document") {
            return null
        }
        const currentPage = (frame.renderConfig?._documentPage ?? 1)
        const flowLength = Array.isArray(dsl.flow) ? dsl.flow.length : 0
        const pageCount = flowLength > 0 ? Math.max(1, flowLength) : 1
        const totalPages = Math.max(1, pageCount)
        return {
            currentPage: Math.max(1, Math.min(totalPages, currentPage)),
            totalPages,
            dsl
        }
    }, [frame?.type, frame?.id, props.parentPageInstance?.id, props.parentPageInstance?.itemsForProcessing, frame?.renderConfig?._documentPage, dataVersion])


    useEffect(() => {
        if (!frame) {
            return
        }
        const storedView =
            viewConfigOwner?.getConfig?.explore?.view ??
            viewConfigOwner?.referenceParameters?.explore?.view
        const matched = CollectionUtils.matchViewConfig(viewConfigs, storedView)
        const nextKey = matched.key ?? (storedView ?? 0)
        setActiveView(nextKey)
        setHideNull(datasetOwner?.referenceParameters?.explore?.hideNull)

    }, [frame?.id, frame, viewConfigs, datasetOwner?.id, viewConfigOwner?.id])
    useEffect(() => {
        clearFilterPaneCache()
        clearAncestorsCache()
    }, [frame?.id, clearFilterPaneCache, clearAncestorsCache])
    useEffect(() => {
        if (!isFiltersPanelOpen && !isPivotsPanelOpen && !isAxisPanelOpen) {
            clearFilterPaneCache()
        }
    }, [
        isFiltersPanelOpen,
        isPivotsPanelOpen,
        isAxisPanelOpen,
        filtersSignature,
        filterableListKey,
        listKey,
        dataVersion,
        shouldComputeCollectionList,
        clearFilterPaneCache
    ])
    useEffect(() => {
        if (!shouldComputeCollectionList || list.length === 0) {
            clearFilterPaneCache()
            clearAncestorsCache()
        }
    }, [
        shouldComputeCollectionList,
        list.length,
        listKey,
        filterableListKey,
        clearFilterPaneCache,
        clearAncestorsCache
    ])
    useEffect(() => {
        return () => {
            clearFilterPaneCache()
            clearAncestorsCache()
        }
    }, [clearFilterPaneCache, clearAncestorsCache])

    function updateFrame() {
        if (props.updateFrameExtents && frame) {
            props.updateFrameExtents(frame)
        }
    }
    const updateDatasetView = useCallback(async (patch) => {
        if (!datasetOwner) {
            return
        }
        const current = datasetOwner.referenceParameters?.datasetView ?? {}
        const next = { ...current, ...patch }
        await datasetOwner.setField("referenceParameters.datasetView", next)
        updateFrame()
    }, [datasetOwner, updateFrame])
    const handleProxyToggle = (value)=>{
        if(!frame || frame.type !== "view"){
            return
        }
        frame.setField("referenceParameters.proxy", value ? true : null)
        updateFrame()
    }
    const addImport = (target) => {
        MainStore().globalPicker({
            root: board,// target.referenceId === 118 ? board : undefined,
            target: target,
            exclude: target.primitives.imports,
            callback: (pick) => {
                target.addRelationship(pick, `imports`)
            },
        })
    }
    function buildParameterOptions(source){
        const options = []
        function walk(node, path = []){
            if(node === null || node === undefined){
                return
            }
            if(typeof node === "object" && !Array.isArray(node)){
                Object.entries(node).forEach(([key, value])=>{
                    walk(value, [...path, key])
                })
            }else{
                if(path.length > 0){
                    const key = path.join(".")
                    if(!options.find(d=>d.id === key)){
                        options.push({id: key, title: key})
                    }
                }
            }
        }
        walk(source?.referenceParameters ?? {})
        return options
    }
    function buildFlowControlConnections(){
        if(!frame){
            return []
        }
        const rawConnections = PrimitiveConfig.getInputMap(frame) ?? []
        const seen = new Set()
        return rawConnections.reduce((acc, entry)=>{
            if(!entry?.sourceId){
                return acc
            }
            const configSourceId = entry.sourceId
            const key = `${entry.inputPin ?? ""}`
            if(seen.has(key)){
                return acc
            }
            seen.add(key)
            const source = mainstore.primitive(configSourceId)
            const parts = [
                source?.title ?? configSourceId,
                entry.sourcePin ? `out:${entry.sourcePin}` : undefined,
                entry.inputPin ? `in:${entry.inputPin}` : undefined
            ].filter(Boolean)
            acc.push({
                key,
                source,
                sourcePin: entry.sourcePin,
                inputPin: entry.inputPin,
                label: parts.join(" · ")
            })
            return acc
        },[])
    }
    async function detachOutputRelationships(pinKey){
        if(!frame){
            return 0
        }
        const map = PrimitiveConfig.getOutputMap(frame) ?? []
        const relevant = map.filter(d=>d.outputPin === pinKey)
        for(const rel of relevant){
            const targetPrim = mainstore.primitive(rel.targetId)
            if(targetPrim){
                await frame.removeRelationship(targetPrim, `outputs.${pinKey}_${rel.targetPin}`)
            }
        }
        return relevant.length
    }
    async function syncFlowControlPins(flowControl, previousKeys = new Set()){
        if(!frame){
            return
        }
        const existingPins = {...(frame.referenceParameters?.outputPins ?? {})}
        const newKeys = new Set((flowControl.states ?? []).map(state=>state.pinKey || state.id))
        const updatedPins = {}
        Object.entries(existingPins).forEach(([key, value])=>{
            if(newKeys.has(key)){
                return
            }
            if(previousKeys.has(key) && !newKeys.has(key)){
                return
            }
            updatedPins[key] = value
        })
        for(const state of flowControl.states ?? []){
            const key = state.pinKey || state.id
            updatedPins[key] = {
                name: state.name ?? key,
                description: state.description,
                types: Array.isArray(state.types) && state.types.length > 0 ? state.types : ["primitive"]
            }
        }
        await frame.setField('referenceParameters.outputPins', updatedPins)
    }
    async function updateFlowControlConfig(mutator){
        if(!frame){
            return
        }
        const current = frame.referenceParameters?.flowControl ?? {}
        const previousKeys = new Set((current.states ?? []).map(state=>state.pinKey || state.id))
        const draft = {
            ...current,
            states: (current.states ?? []).map(state=>({
                ...state,
                conditions: (state.conditions ?? []).map(cond=>({...cond}))
            }))
        }
        mutator(draft)
        const validInputPins = new Set(
            (PrimitiveConfig.getInputMap(frame) ?? [])
                .map(entry=>entry.inputPin)
                .filter(Boolean)
        )
        draft.states = (draft.states ?? []).map(state=>({
            ...state,
            passThroughPins: Array.isArray(state.passThroughPins)
                ? state.passThroughPins.filter(pin=>validInputPins.has(pin))
                : []
        }))
        await frame.setField('referenceParameters.flowControl', draft)
        await syncFlowControlPins(draft, previousKeys)
    }
    function createFlowControlState(existingStates = []){
        const index = existingStates.length + 1
        const suffix = Math.random().toString(36).slice(2, 8)
        return {
            id: `fcs-${suffix}`,
            pinKey: `fcs-${index}-${suffix}`,
            name: `State ${index}`,
            description: "",
            aggregator: "all",
            haltFlow: false,
            types: ["primitive"],
            passThroughPins: [],
            conditions: []
        }
    }
    async function addFlowControlState(){
        if(!frame){
            return
        }
        await updateFlowControlConfig(draft=>{
            const existing = draft.states ?? []
            draft.states = [...existing, createFlowControlState(existing)]
        })
    }
    async function removeFlowControlState(state){
        if(!frame){
            return
        }
        const pinKey = state.pinKey || state.id
        const relationships = await (async ()=>{
            const map = PrimitiveConfig.getOutputMap(frame) ?? []
            const relevant = map.filter(d=>d.outputPin === pinKey)
            return relevant.map(rel=>({
                targetId: rel.targetId,
                targetPin: rel.targetPin
            }))
        })()
        const executeRemoval = async ()=>{
            for(const rel of relationships){
                const targetPrim = mainstore.primitive(rel.targetId)
                if(targetPrim){
                    await frame.removeRelationship(targetPrim, `outputs.${pinKey}_${rel.targetPin}`)
                }
            }
            await updateFlowControlConfig(draft=>{
                draft.states = (draft.states ?? []).filter(s=>s.id !== state.id)
            })
        }
        if(relationships.length > 0){
            mainstore.promptDelete({
                prompt: `Remove state "${state.name}" and disconnect ${relationships.length} downstream link${relationships.length === 1 ? "" : "s"}?`,
                handleDelete: async ()=>{
                    await executeRemoval()
                    return true
                }
            })
        }else{
            await executeRemoval()
        }
    }
    async function updateFlowControlState(stateId, updater){
        await updateFlowControlConfig(draft=>{
            const target = (draft.states ?? []).find(s=>s.id === stateId)
            if(!target){
                return
            }
            updater(target)
        })
    }
    async function addFlowControlCondition(stateId){
        const options = buildFlowControlConnections()
        const defaultConnection = options[0]
        if(!defaultConnection){
            return
        }
        await updateFlowControlConfig(draft=>{
            const state = (draft.states ?? []).find(s=>s.id === stateId)
            if(!state){
                return
            }
            state.conditions = state.conditions ?? []
            const suffix = Math.random().toString(36).slice(2, 8)
            state.conditions.push({
                id: `fc_cond_${suffix}`,
                inputPin: defaultConnection?.inputPin,
                metric: "count",
                comparator: "gte",
                value: 0
            })
        })
    }
    async function updateFlowControlCondition(stateId, conditionId, updater){
        await updateFlowControlConfig(draft=>{
            const state = (draft.states ?? []).find(s=>s.id === stateId)
            if(!state){
                return
            }
            state.conditions = state.conditions ?? []
            const idx = state.conditions.findIndex(c=>c.id === conditionId)
            if(idx === -1){
                return
            }
            const updated = {...state.conditions[idx]}
            updater(updated)
            state.conditions[idx] = updated
        })
    }
    async function removeFlowControlCondition(stateId, conditionId){
        await updateFlowControlConfig(draft=>{
            const state = (draft.states ?? []).find(s=>s.id === stateId)
            if(!state){
                return
            }
            state.conditions = (state.conditions ?? []).filter(c=>c.id !== conditionId)
        })
    }
    async function toggleFlowControlStateType(stateId, type){
        await updateFlowControlConfig(draft=>{
            const state = (draft.states ?? []).find(s=>s.id === stateId)
            if(!state){
                return
            }
            const types = Array.isArray(state.types) ? [...state.types] : []
            if(types.includes(type)){
                state.types = types.filter(t=>t !== type)
            }else{
                state.types = [...types, type]
            }
        })
    }
    async function toggleFlowControlStatePassPin(stateId, inputPin){
        if(!inputPin){
            return
        }
        await updateFlowControlConfig(draft=>{
            const state = (draft.states ?? []).find(s=>s.id === stateId)
            if(!state){
                return
            }
            const pins = Array.isArray(state.passThroughPins) ? [...state.passThroughPins] : []
            if(pins.includes(inputPin)){
                state.passThroughPins = pins.filter(pin=>pin !== inputPin)
            }else{
                state.passThroughPins = [...pins, inputPin]
            }
        })
    }
    const stringComparators = [
        {id: "equals", title: "Equals"},
        {id: "not_equals", title: "Does not equal"},
        {id: "contains", title: "Contains"},
        {id: "not_contains", title: "Does not contain"},
        {id: "starts_with", title: "Starts with"},
        {id: "ends_with", title: "Ends with"}
    ]
    const numericComparators = [
        {id: "gt", title: ">"},
        {id: "gte", title: "≥"},
        {id: "lt", title: "<"},
        {id: "lte", title: "≤"},
        {id: "equals", title: "="},
        {id: "not_equals", title: "≠"}
    ]
    const errorComparators = [
        {id: "equals", title: "Equals"},
        {id: "not_equals", title: "Does not equal"}
    ]
    const errorStatusOptions = [
        {id: "error", title: "Error"},
        {id: "error_ignore", title: "Error (ignored)"},
        {id: "error_skip", title: "Error (skipped)"},
        {id: "complete", title: "Complete"},
        {id: "running", title: "Running"},
        {id: "pending", title: "Pending"},
        {id: "not_run", title: "Not run"}
    ]
    function comparatorOptionsForMetric(metric){
        if(metric === "count"){
            return numericComparators
        }
        if(metric === "error"){
            return errorComparators
        }
        if(metric === "parameter"){
            return [...numericComparators, ...stringComparators]
        }
        return stringComparators
    }
    const renderDescendantPanels = (items, { newPrimitiveCallback: onNewPrimitive, newItemParent, actionAnchor, panelConfig, createNewView, filters } = {}) => {
        if (!items?.length) {
            return null
        }
        const descendants = mainstore.uniquePrimitives(items.flatMap(d => d.primitives?.directDescendants ?? []))
        const descendantCategories = descendants
            .map(d => d.referenceId)
            .filter((d, i, a) => a.indexOf(d) === i)
            .map(d => mainstore.category(d))
            .filter(d => d && ["activity", "evidence", "entity", "result", "detail"].includes(d.primitiveType))

        if (descendantCategories.length === 0) {
            return null
        }

        return (
            <div className="space-y-2">
                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Descendants of items</p>
                <div className="border border-gray-200 dark:border-slate-700 rounded-md p-2 space-y-2">
                    {descendantCategories.map((d) => (
                        <div key={d.id} className="p-2 bg-gray-50 dark:bg-slate-900 text-sm border border-gray-200 dark:border-slate-700 rounded-md ">
                            <CategoryHeader
                                itemCategory={d}
                                newPrimitiveCallback={onNewPrimitive}
                                newItemParent={newItemParent}
                                createNewView={createNewView}
                                panelConfig={panelConfig}
                                actionAnchor={actionAnchor}
                                items={descendants.filter(d2 => d2.referenceId === d.id)}
                                filters={filters}
                            />
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    const [integrationProviders, setIntegrationProviders] = useState(null)
    const [loadingProviders, setLoadingProviders] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const isProxyView = frame?.type === "view" && Boolean(frame.referenceParameters?.proxy)

    let content

    useEffect(() => {
        if (frame?.type === "external" && !integrationProviders && !loadingProviders) {
            setLoadingProviders(true)
            fetch('/api/integrations/providers')
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to load integration providers (${response.status})`)
                    }
                    return response.json()
                })
                .then((body) => {
                    setIntegrationProviders(Array.isArray(body?.providers) ? body.providers : [])
                })
                .catch((error) => {
                    console.error(error)
                    setIntegrationProviders([])
                })
                .finally(() => setLoadingProviders(false))
        }
    }, [frame?.type, integrationProviders, loadingProviders])

    const flowElementControl = () => {
        if (frame.flowElement) {
            function setConfigValue(k, v) {
                const normalizeConfigList = (value) =>
                    [value]
                        .flat()
                        .map((entry)=>`${entry ?? ""}`.trim())
                        .filter(Boolean)
                const sanitizedValue = Array.isArray(v) ? normalizeConfigList(v) : v
                if (k.at(0) === "!") {
                    frame.setField(`referenceParameters.${k.slice(1)}`, sanitizedValue)
                } else {
                    frame.setField(`referenceParameters.fc_${k}`, sanitizedValue)
                }
            }
            const errorOptions = [
                { id: "ignore", title: "Ignore" },
                { id: "stop", title: "Stop flow" },
                frame.type === "search" ? [
                    { id: "stop_25", title: "Stop flow at >= 25% child errors" },
                    { id: "stop_50", title: "Stop flow at >= 50% child errors" },
                    { id: "stop_75", title: "Stop flow at >= 75% child errors" }
                ] : undefined,
                { id: "skip", title: "Skip downstream" },
                frame.type === "search" ? [
                    { id: "skip_25", title: "Skip downstream at >= 25% child errors" },
                    { id: "skip_50", title: "Skip downstream at >= 50% child errors" },
                    { id: "skip_75", title: "Skip downstream at >= 75% child errors" },
                ] : undefined,
            ].flat().filter(Boolean)
            return <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-300 font-medium px-3 p-2 @container">
                <Panel collapsable noSpace title="Control" icon={<AdjustmentsVerticalIcon className="w-5 h-5" />}>
                    <DescriptionList inContainer={true} className="my-2 space-y-2">
                        <DescriptionTerm inContainer={true}>Map view</DescriptionTerm>
                        <DescriptionDetails inContainer={true}>
                            <Checkbox size="sm" isSelected={frame.referenceParameters.fcAllowEdit} onValueChange={(selected) => frame.setField('referenceParameters.fcAllowEdit', selected)}>Allow user to edit</Checkbox>
                        </DescriptionDetails>
                        <DescriptionTerm inContainer={true}>Map view</DescriptionTerm>
                        <DescriptionDetails inContainer={true}>
                            <div className="space-y-2 flex flex-col">
                                <Checkbox size="sm" isSelected={frame.referenceParameters.showInMap !== false} onValueChange={(selected) => frame.setField('referenceParameters.showInMap', selected)}>Show in map</Checkbox>
                                <InputWithSync label="Label" placeholder="Label to show user" variant="bordered" primitive={frame} field="labelForMap" />
                                <InputWithSync label="Label" placeholder="Description" variant="bordered" primitive={frame} field="stepDescription" />
                            </div>
                        </DescriptionDetails>
                        <DescriptionTerm inContainer={true}>Error handling</DescriptionTerm>
                        <DescriptionDetails inContainer={true}>
                            <div className="space-y-2 flex flex-col">
                                <Select className="w-full" label="Error options" variant="bordered" selectedKeys={[frame.referenceParameters?.[`fcHandleError`]].flat() ?? []} onChange={(e) => setConfigValue("!fcHandleError", e.target.value)}>
                                    {errorOptions.map((d) => (
                                        <SelectItem key={d.id}>{d.title}</SelectItem>
                                    ))}
                                </Select>
                                <InputWithSync
                                    primitive={frame}
                                    field="fcErrorMessage"
                                    label="Error message"
                                    placeholder="Custom message to display when this step errors"
                                    variant="bordered"
                                />
                                <Checkbox
                                    size="sm"
                                    isSelected={frame.referenceParameters?.fcWaitForConfirmation === true}
                                    onValueChange={(selected) => frame.setField("referenceParameters.fcWaitForConfirmation", selected)}
                                >
                                    Wait for confirmation
                                </Checkbox>
                            </div>
                        </DescriptionDetails>
                        <DescriptionTerm inContainer={true}>Active Configurations</DescriptionTerm>
                        <DescriptionDetails inContainer={true}>
                            <div className="space-y-2 flex flex-col">
                                {Object.entries(frame.origin.referenceParameters?.configurations ?? {}).map(([key, config]) => (
                                    <Select className="w-full" selectionMode="multiple" label={config.title} variant="bordered" selectedKeys={Array.isArray(frame.referenceParameters?.[`fc_${key}`]) ? frame.referenceParameters?.[`fc_${key}`].filter((entry)=>`${entry ?? ""}`.trim().length > 0) : []} onChange={(e) => setConfigValue(key, e.target.value.split(",").map((entry)=>entry.trim()).filter(Boolean))}>
                                        {config.options.map((d) => (
                                            <SelectItem key={d.id}>{d.title}</SelectItem>
                                        ))}
                                    </Select>
                                ))}
                            </div>
                        </DescriptionDetails>
                    </DescriptionList>
                </Panel>
            </div>
        }
        return <></>
    }

    const activeInfo = !underlying ? <></> : <div className="bg-yellow-100 border border-yellow-100 my-2 px-2 py-2 rounded-md dark:bg-yellow-900/30 dark:border-yellow-800/60">{(() => {
        return <div className="text-sm text-yellow-800 dark:text-yellow-200">Configuration for flow item</div>
    })()}
    </div>
    let underlyingInfo = !underlying ? <></> : <div className="px-2 py-2 mt-4 bg-ccgreen-50 border-ccgreen-200 border rounded-md dark:bg-emerald-900/20 dark:border-emerald-700/60">{(() => {
        let flowinstance = underlying.findParentPrimitives({ type: "flowinstance" })?.[0]
        let title = flowinstance.primitives.imports.allItems[0]?.filterDescription
        const progressText = formatPrimitiveProgress(underlying.progress, underlying.percentageProgress)
        return <>
            <div className="text-sm text-ccgreen-800 dark:text-emerald-200">Items for flow instance {title} (#{underlying.plainId} @ #{flowinstance?.plainId}) </div>
            {progressText && <div className="mt-2 text-sm text-ccgreen-800 dark:text-emerald-200">{progressText}</div>}
            <div className="flex space-x-2 mt-2">
                <div className="flex flex-1 rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden shadow-sm">
                    <button
                        type="button"
                        className="flex-1 py-2 px-3 text-sm font-medium text-gray-700 dark:text-slate-100 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 focus:outline-none"
                        onClick={() => MainStore().doPrimitiveAction(flowinstance, "run_flowinstance_from_step", { from: underlying.id })}
                    >
                        Run from here
                    </button>
                    <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                            <button
                                type="button"
                                className="px-2 py-2 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 border-l border-gray-300 dark:border-slate-700 focus:outline-none"
                            >
                                <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                            </button>
                        </DropdownTrigger>
                        <DropdownMenu aria-label="Run from here options">
                            <DropdownItem key="run" onPress={() => MainStore().doPrimitiveAction(flowinstance, "run_flowinstance_from_step", { from: underlying.id })}>
                                Run from here
                            </DropdownItem>
                            <DropdownItem key="run_force" onPress={() => MainStore().doPrimitiveAction(flowinstance, "run_flowinstance_from_step", { from: underlying.id, force: true })}>
                                Run from here (force)
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
                <div className="flex flex-1 rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden shadow-sm">
                    <button
                        type="button"
                        className="flex-1 py-2 px-3 text-sm font-medium text-gray-700 dark:text-slate-100 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 focus:outline-none"
                        onClick={() => {
                            const configParent = underlying.configParent
                            if (!configParent) return
                            const peers = configParent.primitives?.config?.allItems ?? []
                            const store = MainStore()
                            for (const peer of peers) {
                                const peerFlowInstance = peer.findParentPrimitives?.({ type: "flowinstance" })?.[0]
                                if (peerFlowInstance) {
                                    store.doPrimitiveAction(peerFlowInstance, "run_flowinstance_from_step", { from: peer.id })
                                }
                            }
                        }}
                    >
                        Run for all instances
                    </button>
                    <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                            <button
                                type="button"
                                className="px-2 py-2 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 border-l border-gray-300 dark:border-slate-700 focus:outline-none"
                            >
                                <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                            </button>
                        </DropdownTrigger>
                        <DropdownMenu aria-label="Run for all instances options">
                            <DropdownItem key="run_all" onPress={() => {
                                const configParent = underlying.configParent
                                if (!configParent) return
                                const peers = configParent.primitives?.config?.allItems ?? []
                                const store = MainStore()
                                for (const peer of peers) {
                                    const peerFlowInstance = peer.findParentPrimitives?.({ type: "flowinstance" })?.[0]
                                    if (peerFlowInstance) {
                                        store.doPrimitiveAction(peerFlowInstance, "run_flowinstance_from_step", { from: peer.id })
                                    }
                                }
                            }}>
                                Run for all instances
                            </DropdownItem>
                            <DropdownItem key="run_all_force" onPress={() => {
                                const configParent = underlying.configParent
                                if (!configParent) return
                                const peers = configParent.primitives?.config?.allItems ?? []
                                const store = MainStore()
                                for (const peer of peers) {
                                    const peerFlowInstance = peer.findParentPrimitives?.({ type: "flowinstance" })?.[0]
                                    if (peerFlowInstance) {
                                        store.doPrimitiveAction(peerFlowInstance, "run_flowinstance_from_step", { from: peer.id, force: true })
                                    }
                                }
                            }}>
                                Run for all instances (force)
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>
            {
                (underlying.flowStatus?.status === "error" || underlying.flowStatus?.status === "child_error") && <Button variant="bordered" size="sm" onPress={()=>underlying.setFlow("rerun")}>Clear error</Button>
            }
        </>

    })()}
    </div>
    if (frame.type === "flow") {
        const activeFlowInstance = resolveFlowInstanceSelection(props.flowInstances, frame.referenceParameters?.explore?.view).instance
        if (activeFlowInstance) {
            underlyingInfo = <div className="px-2 py-2 mt-4 bg-ccgreen-50 border-ccgreen-200 border rounded-md dark:bg-emerald-900/20 dark:border-emerald-700/60">
                <Button as="a" variant="ghost" color="primary" href={`/item/${activeFlowInstance.id}`}> View flow instance</Button>
            </div>
        }

    }

    const actionRunnerContext = (() => {
        if (frame?.type !== "actionrunner") {
            return {
                actionRunnerActions: [],
                actionOptions: [],
                activeAction: undefined
            }
        }
        const selectedCategoryIds = [frame?.referenceParameters?.referenceId ?? frame?.referenceParameters?.generator]
            .flat()
            .filter(Boolean)
        const selectedCategories = selectedCategoryIds
            .map((id) => mainstore.category(id))
            .filter((d) => d)
        const itemsForCategories = primitiveForContent?.itemsForProcessing ?? []
        let inputCategories = []
        if (itemsForCategories.length > 0) {
            inputCategories = itemsForCategories
                .map(d => d.referenceId)
                .filter((d, i, a) => a.indexOf(d) === i)
                .map(d => mainstore.category(d))
                .filter(d => d)
        } else {
            // check import
            inputCategories = frame.primitives.imports.allSearch
                .flatMap(d => {
                    const meta = d.metadata
                    if (meta) {
                        return d.getConfig.sources
                            .flatMap(d => meta.parameters?.sources?.options.find(d2 => d2.id === d).resultCategoryId)
                    }
                })
                .filter((d, i, a) => d && a.indexOf(d) === i)
                .map(d => mainstore.category(d))
                .filter(d => d)

        }

        const categoriesForActions = selectedCategories.length > 0 ? selectedCategories : inputCategories
        const actions = categoriesForActions.map(d => d.actions).flat()
        const actionRunnerActions = actions.filter(d => d.actionRunner)
        const actionOptions = actionRunnerActions.map(d => ({
            id: d.key,
            icon: d.icon,
            title: d.title
        }))
        const actionByKey = new Map(actionRunnerActions.map(d => [d.key, d]))
        const activeAction = actionByKey.get(frame.referenceParameters.action)
        return {
            actionRunnerActions,
            actionOptions,
            activeAction
        }
    })()

    if (frame?.type === "actionrunner" && Object.values(frame.metadata?.parameters ?? {}).filter(d => d.type).length === 0) {
        content = <div className="flex flex-col pb-2 px-3">
            <span className="text-gray-400 text-xs mt-0.5 mb-2">#{frame.plainId}  {frame.metadata.title ?? "Search"}</span>
            <div className="p-2">
                <Select
                    label="Action"
                    selectedKeys={frame.referenceParameters.action ? [String(frame.referenceParameters.action)] : []}
                    onChange={(e) => {
                        frame.setParameter("action", e.target.value, false, true)
                    }}
                >
                    {actionRunnerContext.actionOptions.map((opt) => (
                        <SelectItem key={String(opt.id)} textValue={opt.title}>
                            {opt.title}
                        </SelectItem>
                    ))}
                </Select>
            </div>
            <ActionFieldsEditor
                action={actionRunnerContext.activeAction}
                primitive={frame}
                className="px-2 pb-2"
                showLabel
                density="sm"
            />
        </div>

    } else if (frame?.type === "search") {
        const searchCategoryIds = [frame.metadata.resultCategoryId ?? frame.metadata.parameters?.sources?.options?.map(d => d.resultCategoryId) ?? []]
            .flat()
            .filter((id) => id !== undefined && id !== null)
        const searchCategoryIdSet = new Set(searchCategoryIds.map((id) => String(id)))
        const searchResults = (primitiveForContent?.primitives?.strictDescendants ?? [])
            .filter((item) => item && searchCategoryIdSet.has(String(item.referenceId)))
        const searchResultCategory = searchResults[0]?.metadata ?? mainstore.category(searchCategoryIds[0])
        const nestedSearch = primitiveForContent?.primitives?.origin?.uniqueSearch ?? []

        const nestedCallback = (id) => {
            mainstore.doPrimitiveAction(mainstore.primitive(id), "run_search")
        }
        const fetchResults = (id) => {
            const d = mainstore.primitive(id)
            for (const [api, data] of Object.entries(d.processing.bd ?? {})) {
                console.log(`Fetch ${api}`)
                mainstore.doPrimitiveAction(d, "bdcollect", { api })
            }

        }

        content = <div className="flex flex-col pb-2 px-3 space-y-3">
            {activeInfo}
            <span className="text-gray-400 text-xs mt-0.5 mb-2">#{frame.plainId}  {frame.metadata.title ?? "Search"}</span>
            <div className="flex w-full space-x-3 place-items-center">
                <MagnifyingGlassIcon className="h-6" />
                <DebouncedInput
                    size="sm"
                    variant="bordered"
                    value={frame.title}
                    placeholder="Title of search"
                    className="w-full"
                    onChange={(value) => {
                        frame.title = value
                    }}
                />
            </div>
            <div className="w-full my-2">
                <QueryPane primitive={frame} detail={false} />
            </div>
            <div className="w-full">
                <QueryPane primitive={frame} terms={false} detailsVariant="panel" />
            </div>

            {frame.metadata?.nestedSearch && <div className=" space-y-2">
                <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title="Nested Searches">
                    <div className="pb-2">
                        <Table
                            primitive={frame}
                            page={0}
                            pageItems={50}
                            onExpand={(d) => mainstore.sidebarSelect(d)}
                            columns={[
                                { field: 'plainId', title: "ID", width: 50 },
                                { renderType: 'title_with_error', title: "Title", width: 200 },
                                { field: 'count', title: "Results" },
                                {
                                    renderType: "actions", title: "Actions", width: 70, actions: [
                                        (d) => !d.isRunning ? { title: "Run", icon: <PlayCircleIcon />, action: (d) => nestedCallback(d.id) } : undefined,
                                        (d) => !d.isRunning ? { title: "Refetch", icon: <CloudArrowDownIcon />, action: (d) => fetchResults(d.id) } : undefined,
                                        (d) => d.isRunning ? { title: "Cancel", icon: <StopCircleIcon />, action: (d) => fetchResults(d.id) } : undefined,
                                        (d) => d.error ? { title: "Reset", icon: <BackwardIcon />, action: (d) => mainstore.primitive(d.id)?.setField("processing.query.status", "rerun") } : undefined,
                                    ]
                                },
                            ]}
                            data={nestedSearch.map(target => {
                                const linkedItemId = Object.entries(target._parentPrimitives ?? {}).find(d => d[1].find(d => d.startsWith("primitives.search.")))?.[0]
                                const linkedItem = linkedItemId ? mainstore.primitive(linkedItemId) : undefined
                                if (target) {
                                    return {
                                        id: target.id,
                                        plainId: target.plainId,
                                        error: target.processing?.query?.error,
                                        isRunning: target.isRunning,
                                        title: linkedItem ? `Search for ${linkedItem.title}` : target.title,
                                        count: target.primitives.origin.allUniqueIds.length,
                                        data: {
                                            id: target.id,
                                            primitive: linkedItem ?? target
                                        }
                                    }
                                }
                            }).filter(d => d)}
                            primitives={frame.primitives.origin.uniqueSearch}
                            className='w-full min-h-[24em] max-h-[60vh] !text-xs' />
                    </div>
                </Panel>
            </div>
            }
            {flowElementControl()}
            {underlyingInfo}
            <div className="w-full border bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 rounded-lg mt-2 p-2">
                <CategoryHeader itemCategory={searchResultCategory} items={searchResults} actionAnchor={frame} />
            </div>
            {renderDescendantPanels(searchResults, {
                newPrimitiveCallback,
                newItemParent: frame.type === "query" ? frame : board,
                actionAnchor: frame,
                panelConfig: frame?.referenceParameters?.table,
                createNewView: props.createNewView,
                filters
            })}
            <QueryPane.Info primitive={frame} />
            <div className="flex space-x-3 mt-3">
                {(searchResults.length > 0) && <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 "
                    onClick={() => mainstore.promptDelete({ prompt: `Remove search all ${searchResults.length} results?`, handleDelete: () => frame.removeChildren() })}
                >
                    Delete {searchResultCategory?.plural ?? (searchResultCategory?.title ? `${searchResultCategory.title}s` : "results")}
                </button>}
                <button
                    type="button"
                    className="flex space-x-2 place-items-center justify-center w-full rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 py-2 px-4 text-sm font-medium text-gray-700 dark:text-slate-100 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => mainstore.doPrimitiveAction(frame, "run_search")}
                >
                    <ArrowPathIcon className="h-4 w-4" aria-hidden="true" /><p>Search</p>
                </button>
            </div>
        </div>

    } else if (frame?.type === "external") {
        const externalTarget = underlying ?? frame
        const providerName = externalTarget?.getConfig?.provider ?? externalTarget?.referenceParameters?.provider;
        const providerInfo = integrationProviders?.find((entry) => entry.name === providerName);
        const syncStatus = externalTarget?.processing?.integration?.sync?.status
        const isSyncRunning = syncStatus === 'running' || syncStatus === 'pending' || syncing
        const syncError = externalTarget?.processing?.integration?.sync?.error
        const syncSummary = externalTarget?.processing?.integration?.sync?.summary

        const handleSync = async () => {
            if (!externalTarget?.id) return
            setSyncing(true)
            try {
                const response = await fetch(`/api/integrations/external/${externalTarget.id}/sync`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ force: true }),
                })
                if (!response.ok) {
                    throw new Error(`Sync request failed (${response.status})`)
                }
                console.info(`Force sync queued for external primitive ${externalTarget.id}`)
            } catch (error) {
                console.error('Failed to trigger sync', error)
            } finally {
                setSyncing(false)
            }
        }

        content = (
            <div className="flex flex-col space-y-4 py-3">
                {activeInfo}
                <div className="rounded-lg border border-default-200 bg-default-100/50 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold text-foreground">Integration Configuration</h3>
                            <p className="mt-1 text-sm text-default-500">
                                Configure how settings for this integration provider.
                            </p>
                        </div>
                        <Button
                            color="primary"
                            size="sm"
                            isDisabled={isSyncRunning}
                            isLoading={isSyncRunning}
                            startContent={!isSyncRunning && <CloudArrowDownIcon className="h-4 w-4" />}
                            onPress={handleSync}
                        >
                            {isSyncRunning ? 'Syncing…' : 'Sync now'}
                        </Button>
                    </div>
                    {syncStatus === 'complete' && syncSummary && (
                        <p className="mt-2 text-xs text-success-600">
                            Last sync: {syncSummary.created ?? 0} created, {syncSummary.updated ?? 0} updated ({syncSummary.processed ?? 0} processed)
                        </p>
                    )}
                    {syncStatus === 'error' && syncError && (
                        <p className="mt-2 text-xs text-danger-600">Sync failed: {syncError}</p>
                    )}
                    <div className="mt-3">
                        <ExternalPrimitiveConfigurator
                            primitive={externalTarget}
                            provider={providerInfo}
                            loadingProviders={loadingProviders}
                            integrationProviders={integrationProviders}
                        />
                    </div>
                </div>
            </div>
        )
    } else if (frame) {
        const viewConfig = resolvedViewSelection.config
        const activeViewKey = resolvedViewSelection.key



        let itemCategoryId = list.map(d => d.referenceId).filter((d, i, a) => d && a.indexOf(d) === i)
        if (itemCategoryId.legnth > 1) {
            console.log(`Multiple catgegory type in list`)
        }
        itemCategoryId = itemCategoryId[0]
        let itemCategory = mainstore.category(itemCategoryId)

        const newItemParent = frame.type === "query" ? frame : board

        newPrimitiveCallback = (d) => {
            if (filters) {
                const newItem = {
                    target: d,
                    importConfig: [{
                        id: frame.id,
                        filters: filters
                    }]
                }
                d = newItem
            }
            if (props.newPrimitiveCallback) {
                props.newPrimitiveCallback(frame, d)
            }
        }

        function getAncestors() {
            if (!shouldComputeCollectionList || list.length === 0) {
                return [[], []]
            }
            const cacheKey = `${frame?.id ?? "no-frame"}|${listKey}|${dataVersion}`
            if (ancestorsCacheRef.current.key === cacheKey) {
                return ancestorsCacheRef.current.value
            }
            let sourceList = list
            /*const origins = list.map(d=>d.origin)
            const originType = origins.map(d=>d.type).filter((d,i,a)=>a.indexOf(d)===i)
            if( originType.length === 1 && originType[0] === "segment"){
                sourceList = origins.flat()
            }*/
            const axisOptions = CollectionUtils.axisFromCollection(sourceList, frame)
            const axisForPivot = axisOptions.filter(d => ["result", "entity", "evidence"].includes(d.category?.primitiveType)).filter((d, i, a) => a.findIndex(d2 => d2.category.id === d.category.id) === i).map(d => ({ category: d.category, relationship: d.relationship }))
            let parents = mainstore.uniquePrimitives(sourceList.map(d => d.findParentPrimitives({ type: ["entity", "result"], first: true })).flat(Infinity))
            const payload = [axisForPivot, parents]
            ancestorsCacheRef.current = { key: cacheKey, value: payload }
            return payload
        }

        function clearItems(e) {
            e.stopPropagation();
            const pathMap = {}
            list.forEach(d => {
                (d.parentPaths(frame) ?? []).forEach(p => {
                    if (p === "ref" || p === "link" || p === "origin") {
                        pathMap[p] ||= []
                        pathMap[p].push(d)
                    }
                })
            })
            for (const path of Object.keys(pathMap)) {
                if (path !== "origin") {
                    for (const d of pathMap[path]) {
                        frame.removeRelationship(d, path)
                    }
                }
            }
            if (pathMap.origin) {
                mainstore.promptDelete({
                    prompt: `Delete ${list.length} items?`,
                    handleDelete: async () => {
                        for (const d of pathMap.origin) {
                            await mainstore.removePrimitive(d)
                        }
                        return true
                    }
                })
            }
        }

        const updateViewMode = (id) => {
            const match = CollectionUtils.matchViewConfig(viewConfigs, id)
            const newConfig = match.config
            if (!newConfig) {
                return
            }
            const newKey = match.key ?? id
            const targetOwner = viewConfigOwner ?? frame
            if (!targetOwner) {
                return
            }
            if (newConfig.categoryId !== undefined && newConfig.categoryId !== targetOwner.referenceParameters?.explore?.viewCategory) {
                targetOwner.setField(`referenceParameters.explore.viewCategory`, newConfig.categoryId)
            }
            targetOwner.setField(`referenceParameters.explore.view`, newKey)
            setActiveView(newKey)
            if (props.updateFrameExtents) {
                //props.updateFrameExtents( frame )
            }
        }
        const addCategory = (target) => {
            const baseCategories = [54, 90, 166]
            MainStore().globalCategoryPicker({
                categoryIds: baseCategories,
                callback: async (d) => {

                    const newPrim = await mainstore.createPrimitive({ type: 'category', parent: target, categoryId: d.categoryId, referenceParameters: { target: "items" } })
                    if (newPrim) {
                        await mainstore.waitForPrimitive(newPrim.id)
                        mainstore.globalCategoryEditor({ primitive: newPrim, originTask: target })
                    }

                    return true
                }
            })

        }
        function filterPane() {
            const primitiveForFilter = frame
            const fullList = filterableList ?? []
            if (!primitiveForFilter) {
                return { filters: null, allocations: null, axis: null }
            }

            const columnAxis = CollectionUtils.primitiveAxis(primitiveForFilter, "column", fullList)
            const rowAxis = CollectionUtils.primitiveAxis(primitiveForFilter, "row", fullList)
            const colFilter = PrimitiveConfig.decodeExploreFilter(primitiveForFilter.referenceParameters?.explore?.axis?.column?.filter)
            const rowFilter = PrimitiveConfig.decodeExploreFilter(primitiveForFilter.referenceParameters?.explore?.axis?.row?.filter)

            const viewFilters = primitiveForFilter.referenceParameters?.explore?.filters?.map((d2, i) => CollectionUtils.primitiveAxis(primitiveForFilter, i, fullList)) ?? []
            let viewPivot = primitiveForFilter.referenceParameters?.explore?.viewPivot
            const axisOptions = CollectionUtils.axisFromCollection(fullList, primitiveForFilter).map(d => {
                const out = { ...d }
                if (d.relationship) {
                    out.relationship = [d.relationship].flat()//.map(d=>d.split(":")[0])
                    out.access = [out.relationship].flat().length
                }
                return out
            })
            const localFilters = CollectionUtils.getExploreFilters(primitiveForFilter, axisOptions)

            let liveFilters = primitiveForFilter.primitives.allUniqueCategory.filter(d => d.referenceId === PrimitiveConfig.Constants["LIVE_FILTER"]).map(d => {
                return {
                    type: "category",
                    primitiveId: d.id,
                    category: d,
                    isLive: true,
                    title: `Category: ${d.title}`
                }
            })

            const { extents } = CollectionUtils.mapCollectionByAxis(
                fullList,
                columnAxis,
                rowAxis,
                viewFilters,
                liveFilters,
                viewPivot,
                { includeData: false, trimAxisValues: true }
            )
            const orderedAxisValues = {
                column: CollectionUtils.computeOrderedAxisEntries(extents?.column, primitiveForFilter.referenceParameters?.explore?.axis?.column?.order),
                row: CollectionUtils.computeOrderedAxisEntries(extents?.row, primitiveForFilter.referenceParameters?.explore?.axis?.row?.order)
            }

            const sets = [
                { selection: "column", mode: "column", title: "Columns", list: colFilter, axis: columnAxis },
                { selection: "row", mode: "row", title: "Rows", list: rowFilter, axis: rowAxis },
                ...localFilters.map((d, idx) => ({ axis: axisOptions[d.option], selection: `filterGroup${idx}`, title: `Filter by ${axisOptions[d.option]?.title}`, deleteIdx: idx, treatment: d.treatment, mode: idx, list: d.filter }))
            ]

            const axisSets = sets.slice(0, 2)
            const viewFilterSets = sets.slice(2)

            const forFilters = viewFilterSets.filter((d, i) => d.treatment === "filter" || (d.treatment === undefined && i !== 0))
            const forAllocations = viewFilterSets.filter((d, i) => d.treatment === "allocation" || (d.treatment === undefined && i === 0))

            const filterOptions = {
                mainstore: mainstore,
                updateAxisFilter: (item, mode, setAll, axisExtents) => {
                    let currentFilter
                    if (mode === "row") {
                        currentFilter = rowFilter
                    } else if (mode === "column") {
                        currentFilter = colFilter
                    } else {
                        currentFilter = PrimitiveConfig.decodeExploreFilter(primitiveForFilter.referenceParameters?.explore?.filters?.[mode]?.filter)
                    }
                    CollectionUtils.updateAxisFilter(primitiveForFilter, mode, currentFilter, item, setAll, axisExtents,
                        () => {
                            if (props.updateFrameExtents) {
                                props.updateFrameExtents(primitiveForFilter)
                            }
                        }
                    )
                },
                deleteViewFilter: (idx) => {
                    const filter = viewFilters[idx]
                    let filters = primitiveForFilter.referenceParameters?.explore?.filters
                    filters = filters.filter(d => d.track !== filter.track)

                    primitiveForFilter.setField("referenceParameters.explore.filters", filters)
                    if (props.updateFrameExtents) {
                        props.updateFrameExtents(primitiveForFilter)
                    }
                }

            }

            return {
                axis: axisPaneContent({ axisSets, extents, filterOptions, orderedAxisValues }),
                filters: filterAllocationsPane({ mode: "filter", sets: forFilters, extents, axisOptions, rowFilter, colFilter, viewFilters, totalItems: fullList.length, filterOptions }),
                allocations: filterAllocationsPane({ mode: "allocation", sets: forAllocations, extents, axisOptions, rowFilter, colFilter, viewFilters, totalItems: fullList.length, filterOptions }),
            }
        }
        function getFilterPanels() {
            if (!shouldComputeCollectionList) {
                return null
            }
            const explore = frame?.referenceParameters?.explore
            const cacheKey = [
                frame?.id ?? "no-frame",
                frame?.type ?? "no-type",
                filtersSignature,
                hideNull ? "1" : "0",
                filterableListKey,
                listKey,
                dataVersion,
                explore?.view ?? "0",
                explore?.filterTrack ?? "0",
                safeStringify(explore?.viewPivot ?? null)
            ].join("|")
            if (filterPaneCacheRef.current.key === cacheKey) {
                return filterPaneCacheRef.current.panels
            }
            const panels = filterPane() ?? { axis: null, filters: null, allocations: null }
            filterPaneCacheRef.current = { key: cacheKey, panels }
            return panels
        }
        function axisPaneContent({ axisSets, extents, filterOptions, orderedAxisValues }) {
            if (!axisSets?.length) {
                return null
            }
            const axisOwner = frame?.configParent ?? frame
            return (
                <div className="space-y-2">
                    {axisSets.map((set) => {
                        const axisExtents = extents?.[set.selection] ?? []
                        const axisConfig = axisOwner?.referenceParameters?.explore?.axis?.[set.selection] ?? {}
                        return (
                            <Panel collapsable noSpace key={`axis-${set.selection}`} title={set.title}>
                                <AxisSelectionList
                                    axisOwner={axisOwner}
                                    axisName={set.selection}
                                    axisTitle={set.title}
                                    axisExtents={axisExtents}
                                    axisConfig={axisConfig}
                                    filterState={set.list}
                                    orderedAxisValues={orderedAxisValues?.[set.selection]}
                                    onSelectAll={() => filterOptions.updateAxisFilter(false, set.mode, true, axisExtents)}
                                    onClearAll={() => filterOptions.updateAxisFilter(true, set.mode, true, axisExtents)}
                                    onToggleValue={(value) => filterOptions.updateAxisFilter(value, set.mode, false, axisExtents)}
                                />
                            </Panel>
                        )
                    })}
                </div>
            )
        }
        function AxisSelectionList({ axisName, axisTitle, axisExtents, axisConfig, filterState, orderedAxisValues, onSelectAll, onClearAll, onToggleValue, axisPath: customAxisPath }) {
            const [localOrder, setLocalOrder] = useState(null)
            const [draggingValue, setDraggingValue] = useState(null)
            const [dragOverValue, setDragOverValue] = useState(null)
            const axisValueMap = useMemo(() => {
                const map = new Map()
                ;(axisExtents ?? []).forEach(entry => {
                    map.set(entry.idx, entry)
                })
                ;(orderedAxisValues ?? []).forEach(entry => {
                    if (!map.has(entry.idx)) {
                        map.set(entry.idx, entry)
                    }
                })
                return map
            }, [axisExtents, orderedAxisValues])
            const datatableValueMap = useMemo(() => {
                const map = new Map()
                ;(orderedAxisValues ?? []).forEach(entry => {
                    map.set(entry.idx, entry)
                })
                return map
            }, [orderedAxisValues])
            const storedOrder = axisConfig?.order
            const derivedOrderMode = useMemo(() => {
                if (Array.isArray(storedOrder)) {
                    return "manual"
                }
                if (storedOrder && typeof storedOrder === "object") {
                    if (storedOrder.field === "value" || storedOrder.field === "count") {
                        return "count"
                    }
                    return "alpha"
                }
                return "alpha"
            }, [storedOrder])
            const resolvedOrderMode = axisConfig?.orderMode ?? derivedOrderMode
            const [localOrderMode, setLocalOrderMode] = useState(null)
            useEffect(() => {
                setLocalOrderMode(null)
            }, [resolvedOrderMode])
            const orderMode = localOrderMode ?? resolvedOrderMode
            const storedDirection = axisConfig?.orderDirection ?? ((storedOrder && typeof storedOrder === "object" && !Array.isArray(storedOrder) && storedOrder.descend) ? "desc" : "asc")
            const orderDescend = storedDirection === "desc"
            const allowManualOrdering = orderMode === "manual"
            const axisPath = customAxisPath ?? `referenceParameters.explore.axis.${axisName}`
            const limitDisplayValue = Number.isFinite(axisConfig?.limit) && axisConfig.limit > 0 ? axisConfig.limit : ""
            const countFilterMode = axisConfig?.countFilter?.mode ?? "gte"
            const countFilterDisplayValue = Number.isFinite(axisConfig?.countFilter?.value) ? axisConfig.countFilter.value : ""
            const computedOrder = useMemo(() => {
                const datatableOrder = (orderedAxisValues ?? []).map(entry => entry.idx)
                if (datatableOrder.length > 0) {
                    const extras = (axisExtents ?? []).map(entry => entry.idx).filter(value => !datatableOrder.includes(value))
                    return [...datatableOrder, ...extras]
                }
                const baseOrder = Array.isArray(storedOrder) ? storedOrder : []
                const presentStored = baseOrder.filter(value => axisValueMap.has(value))
                const extentOrder = (axisExtents ?? []).map(entry => entry.idx)
                const appended = extentOrder.filter(value => !presentStored.includes(value))
                const missingStored = baseOrder.filter(value => !axisValueMap.has(value))
                return [...presentStored, ...appended, ...missingStored]
            }, [axisExtents, axisValueMap, orderedAxisValues, storedOrder])
            const displayOrder = localOrder ?? computedOrder
            const orderSignature = useMemo(() => (computedOrder ?? []).map(value => (value ?? "_N_")).join("|"), [computedOrder])
            useEffect(() => {
                setLocalOrder(null)
            }, [orderSignature, axisName])

            const persistOrder = useCallback(async (order) => {
                if (!frame || orderMode !== "manual") {
                    return
                }
                const deduped = order.filter((value, index) => order.indexOf(value) === index)
                await frame.setField(`${axisPath}.order`, deduped.length ? deduped : null)
                await frame.setField(`${axisPath}.manualOrder`, deduped.length ? deduped : null)
                updateFrame()
            }, [axisPath, frame, orderMode])

            const reorderToIndex = useCallback(async (sourceValue, rawTargetIndex) => {
                if (!allowManualOrdering) {
                    return
                }
                const current = [...(localOrder ?? computedOrder)]
                const fromIndex = current.indexOf(sourceValue)
                let targetIndex = rawTargetIndex
                if (fromIndex === -1) {
                    return
                }
                if (targetIndex > current.length) {
                    targetIndex = current.length
                }
                const [moved] = current.splice(fromIndex, 1)
                const adjustedIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex
                current.splice(Math.max(0, adjustedIndex), 0, moved)
                setLocalOrder(current)
                await persistOrder(current)
            }, [allowManualOrdering, computedOrder, localOrder, persistOrder])

            const handleDropOnValue = useCallback(async (value) => {
                if (!allowManualOrdering || !draggingValue || draggingValue === value) {
                    return
                }
                const targetIndex = (localOrder ?? computedOrder).indexOf(value)
                if (targetIndex === -1) {
                    return
                }
                await reorderToIndex(draggingValue, targetIndex)
                setDraggingValue(null)
                setDragOverValue(null)
            }, [allowManualOrdering, computedOrder, draggingValue, localOrder, reorderToIndex])

            const handleDropAtEnd = useCallback(async () => {
                if (!allowManualOrdering || !draggingValue) {
                    return
                }
                await reorderToIndex(draggingValue, (localOrder ?? computedOrder).length)
                setDraggingValue(null)
                setDragOverValue(null)
            }, [allowManualOrdering, computedOrder, draggingValue, localOrder, reorderToIndex])

            const handleDragStart = useCallback((value) => (event) => {
                if (!allowManualOrdering) {
                    return
                }
                event.dataTransfer.effectAllowed = "move"
                try {
                    event.dataTransfer.setData("text/plain", `${value ?? "_N_"}`)
                } catch {
                    // ignore
                }
                setDraggingValue(value)
            }, [allowManualOrdering])

            const handleDragOver = useCallback((value) => (event) => {
                event.preventDefault()
                if (dragOverValue !== value) {
                    setDragOverValue(value)
                }
            }, [dragOverValue])

            const handleDragLeave = useCallback((value) => () => {
                if (dragOverValue === value) {
                    setDragOverValue(null)
                }
            }, [dragOverValue])

            const handleDrop = useCallback((value) => async (event) => {
                event.preventDefault()
                await handleDropOnValue(value)
                setDraggingValue(null)
                setDragOverValue(null)
            }, [handleDropOnValue])

            const handleDragEnd = useCallback(() => {
                setDragOverValue(null)
                setDraggingValue(null)
            }, [])

            const handleDropZoneOver = useCallback((event) => {
                event.preventDefault()
                if (dragOverValue !== "__END__") {
                    setDragOverValue("__END__")
                }
            }, [dragOverValue])

            const handleDropZoneLeave = useCallback(() => {
                if (dragOverValue === "__END__") {
                    setDragOverValue(null)
                }
            }, [dragOverValue])

            const handleOrderModeChange = useCallback(async (mode) => {
                if (!frame) {
                    return
                }
                if (mode === "manual") {
                    const fallbackOrder = axisConfig?.manualOrder ?? (Array.isArray(storedOrder) ? storedOrder : (localOrder ?? computedOrder))
                    await frame.setField(`${axisPath}.order`, fallbackOrder ?? [])
                    await frame.setField(`${axisPath}.manualOrder`, fallbackOrder ?? [])
                } else {
                    const field = mode === "count" ? "value" : "_label"
                    if (Array.isArray(storedOrder)) {
                        await frame.setField(`${axisPath}.manualOrder`, storedOrder)
                    }
                    await frame.setField(`${axisPath}.order`, { field, descend: orderDescend })
                }
                await frame.setField(`${axisPath}.orderMode`, mode)
                setLocalOrderMode(mode)
                updateFrame()
            }, [axisConfig?.manualOrder, axisPath, computedOrder, frame, localOrder, orderDescend, storedOrder, updateFrame])

            const handleOrderDirectionChange = useCallback(async (direction) => {
                if (!frame) {
                    return
                }
                await frame.setField(`${axisPath}.orderDirection`, direction)
                if (orderMode !== "manual") {
                    const current = (storedOrder && typeof storedOrder === "object" && !Array.isArray(storedOrder) ? storedOrder.field : undefined) ?? (orderMode === "count" ? "value" : "_label")
                    await frame.setField(`${axisPath}.order`, { field: current, descend: direction === "desc" })
                }
                updateFrame()
            }, [axisPath, frame, orderMode, storedOrder, updateFrame])

            const handleLimitChange = useCallback(async (value) => {
                if (!frame) {
                    return
                }
                const trimmed = typeof value === "string" ? value.trim() : value
                const numeric = Number(trimmed)
                const nextValue = trimmed === "" || !Number.isFinite(numeric) || numeric <= 0 ? null : numeric
                await frame.setField(`${axisPath}.limit`, nextValue)
                updateFrame()
            }, [axisPath, frame, updateFrame])

            const handleCountFilterModeChange = useCallback(async (mode) => {
                if (!frame) {
                    return
                }
                const currentValue = axisConfig?.countFilter?.value
                if (Number.isFinite(currentValue)) {
                    await frame.setField(`${axisPath}.countFilter`, { mode, value: currentValue })
                } else {
                    await frame.setField(`${axisPath}.countFilter`, { mode })
                }
                updateFrame()
            }, [axisConfig?.countFilter?.value, axisPath, frame, updateFrame])

            const handleCountFilterValueChange = useCallback(async (value) => {
                if (!frame) {
                    return
                }
                const numeric = Number(value)
                if (Number.isFinite(numeric)) {
                    await frame.setField(`${axisPath}.countFilter`, { mode: countFilterMode, value: numeric })
                } else if (axisConfig?.countFilter?.mode) {
                    await frame.setField(`${axisPath}.countFilter`, { mode: axisConfig.countFilter.mode })
                } else {
                    await frame.setField(`${axisPath}.countFilter`, null)
                }
                updateFrame()
            }, [axisConfig?.countFilter?.mode, axisPath, countFilterMode, frame, updateFrame])

            const handleClearCountFilter = useCallback(async () => {
                if (!frame) {
                    return
                }
                await frame.setField(`${axisPath}.countFilter`, null)
                updateFrame()
            }, [axisPath, frame, updateFrame])

            const handleNullLabelChange = useCallback(async (value) => {
                if (!frame) {
                    return
                }
                const trimmed = typeof value === "string" ? value.trim() : ""
                await frame.setField(`${axisPath}.nullLabel`, trimmed.length ? trimmed : null)
                updateFrame()
            }, [axisPath, frame, updateFrame])
            const isMetricAxis = axisConfig?.type === "metric_fields"
            const metricFields = useMemo(() => Array.isArray(axisConfig?.fields) ? axisConfig.fields : [], [axisConfig?.fields])
            const metricAggregate = (axisConfig?.aggregate ?? "sum").toLowerCase()
            const metricFieldKey = useCallback((field, index) => field?.idx ?? field?.parameter ?? field?.path ?? field?.label ?? field?.title ?? `metric_${index}`, [])
            const activeMetricFieldCount = useMemo(() => metricFields.filter(field => field && field.disabled !== true).length, [metricFields])
            const handleMetricAggregateChange = useCallback(async (value) => {
                if (!frame) {
                    return
                }
                await frame.setField(`${axisPath}.aggregate`, value)
                updateFrame()
            }, [axisPath, frame, updateFrame])
            const handleMetricFieldToggle = useCallback(async (fieldKey, nextEnabled) => {
                if (!frame) {
                    return
                }
                if (!nextEnabled && activeMetricFieldCount <= 1) {
                    return
                }
                const updated = metricFields.map((field, index) => {
                    if (!field) {
                        return field
                    }
                    const key = metricFieldKey(field, index)
                    if (key !== fieldKey) {
                        return field
                    }
                    const next = { ...field }
                    if (nextEnabled) {
                        delete next.disabled
                    } else {
                        next.disabled = true
                    }
                    return next
                })
                await frame.setField(`${axisPath}.fields`, updated)
                updateFrame()
            }, [activeMetricFieldCount, axisPath, frame, metricFieldKey, metricFields, updateFrame])

            const filterMap = filterState ?? {}
            const hasValues = displayOrder.length > 0
            const hasCustomOrder = Array.isArray(storedOrder) && storedOrder.length > 0
            const customNullLabel = axisConfig?.nullLabel?.trim()

            return (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-700">{axisTitle}</p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                className="rounded-full bg-white dark:bg-slate-950 px-2.5 py-1 text-xs font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-slate-900"
                                onClick={onSelectAll}
                            >
                                Select all
                            </button>
                            <button
                                type="button"
                                className="rounded-full bg-white dark:bg-slate-950 px-2.5 py-1 text-xs font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-slate-900"
                                onClick={onClearAll}
                            >
                                Clear all
                            </button>
                        </div>
                    </div>
                    <div className="rounded-md border border-default-200 bg-white dark:bg-slate-950 p-3 space-y-3">
                        {isMetricAxis && (
                            <div className="space-y-3">
                                <label className="flex flex-col text-xs font-semibold text-gray-600">
                                    Metric aggregation
                                    <select
                                        className="mt-1 rounded-md border border-default-200 px-2 py-1 text-sm text-gray-700"
                                        value={metricAggregate}
                                        onChange={(event) => handleMetricAggregateChange(event.target.value)}
                                    >
                                        <option value="sum">Sum</option>
                                        <option value="avg">Average</option>
                                        <option value="min">Minimum</option>
                                        <option value="max">Maximum</option>
                                    </select>
                                </label>
                                <div className="flex flex-col gap-2">
                                    <p className="text-xs font-semibold text-gray-600">Metric fields</p>
                                    {metricFields.length === 0 && (
                                        <p className="text-xs text-gray-500">No metric-capable fields were detected for this data set.</p>
                                    )}
                                    {metricFields.length > 0 && (
                                        <div className="grid gap-1">
                                            {metricFields.map((field, index) => {
                                                if (!field) {
                                                    return null
                                                }
                                                const key = metricFieldKey(field, index)
                                                const enabled = field.disabled !== true
                                                const label = field.label ?? field.title ?? field.parameter ?? key
                                                return (
                                                    <Checkbox
                                                        key={`${axisName}-metric-${key}`}
                                                        size="sm"
                                                        isSelected={enabled}
                                                        onValueChange={(value) => handleMetricFieldToggle(key, value)}
                                                    >
                                                        <span className="text-xs text-gray-700">{label}</span>
                                                    </Checkbox>
                                                )
                                            })}
                                            {activeMetricFieldCount <= 1 && (
                                                <p className="text-[11px] text-gray-500">Keep at least one metric selected.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2">
                            <label className="flex flex-col text-xs font-semibold text-gray-600">
                                Order mode
                                <select
                                    className="mt-1 rounded-md border border-default-200 px-2 py-1 text-sm text-gray-700"
                                    value={orderMode}
                                    onChange={(event) => handleOrderModeChange(event.target.value)}
                                >
                                    <option value="alpha">Alphabetical</option>
                                    <option value="count">By total count</option>
                                    <option value="manual">Manual</option>
                                </select>
                            </label>
                            {orderMode !== "manual" && (
                                <label className="flex flex-col text-xs font-semibold text-gray-600">
                                    Sort direction
                                    <select
                                        className="mt-1 rounded-md border border-default-200 px-2 py-1 text-sm text-gray-700"
                                        value={orderDescend ? "desc" : "asc"}
                                        onChange={(event) => handleOrderDirectionChange(event.target.value)}
                                    >
                                        <option value="asc">Ascending</option>
                                        <option value="desc">Descending</option>
                                    </select>
                                </label>
                            )}
                        </div>
                        {orderMode !== "manual" && (
                            <label className="flex flex-col text-xs font-semibold text-gray-600">
                                Limit results
                                <DebouncedNumberInput
                                    className="mt-1"
                                    min={1}
                                    value={limitDisplayValue}
                                    onValueChange={handleLimitChange}
                                    placeholder="Unlimited"
                                />
                            </label>
                        )}
                        <div className="grid gap-2 sm:grid-cols-[auto,1fr,auto] items-end">
                            <label className="flex flex-col text-xs font-semibold text-gray-600">
                                Filter by count
                                <select
                                    className="mt-1 rounded-md border border-default-200 px-2 py-1 text-sm text-gray-700"
                                    value={countFilterMode}
                                    onChange={(event) => handleCountFilterModeChange(event.target.value)}
                                >
                                    <option value="gte">At least</option>
                                    <option value="lte">At most</option>
                                </select>
                            </label>
                            <DebouncedNumberInput
                                className="mt-1"
                                min={0}
                                value={countFilterDisplayValue}
                                onValueChange={handleCountFilterValueChange}
                                placeholder="Enter count"
                            />
                            <button
                                type="button"
                                className="rounded-md border border-default-200 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:bg-slate-900 disabled:opacity-50"
                                onClick={handleClearCountFilter}
                                disabled={!axisConfig?.countFilter}
                            >
                                Clear
                            </button>
                        </div>
                        <label className="flex flex-col text-xs font-semibold text-gray-600">
                            Empty value label
                            <DebouncedInput
                                className="mt-1"
                                value={axisConfig?.nullLabel ?? ""}
                                placeholder="None"
                                onChange={handleNullLabelChange}
                            />
                        </label>
                    </div>
                    {!hasValues && (
                        <p className="text-sm text-gray-500">No values available for this axis.</p>
                    )}
                    {hasValues && (
                        <div className="rounded-md border border-default-200 divide-y divide-default-200 bg-white dark:bg-slate-950">
                            {displayOrder.map((value) => {
                                const entry = axisValueMap.get(value) ?? datatableValueMap.get(value)
                                const valueKey = value === undefined ? "_N_" : value
                                const isEmptyValue = value === undefined || value === null || value === "_N_"
                                const label = isEmptyValue
                                    ? (customNullLabel || entry?.label || "Empty")
                                    : (entry?.label ?? `${value}`)
                                const count = entry?.count
                                const isChecked = !(filterMap && filterMap[valueKey] !== undefined)
                                const isDropTarget = dragOverValue === value && draggingValue !== value
                                return (
                                    <div
                                        key={`${axisName}-${valueKey}`}
                                        className={classNames(
                                            "flex items-center gap-2 px-2 py-1.5",
                                            isDropTarget ? "ring-2 ring-ccgreen-400 bg-ccgreen-50" : "",
                                            draggingValue === value ? "opacity-60" : ""
                                        )}
                                        onDragOver={allowManualOrdering ? handleDragOver(value) : undefined}
                                        onDrop={allowManualOrdering ? handleDrop(value) : undefined}
                                        onDragLeave={allowManualOrdering ? handleDragLeave(value) : undefined}
                                    >
                                        {allowManualOrdering && (
                                            <div className="text-gray-400 p-1 w-5 flex justify-center">
                                                <span
                                                    className="cursor-grab"
                                                    draggable
                                                    onDragStart={handleDragStart(value)}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <Bars3Icon className="w-4 h-4" />
                                                </span>
                                            </div>
                                        )}
                                        <label className="flex flex-1 items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="accent-ccgreen-700"
                                                checked={isChecked}
                                                onChange={() => onToggleValue(value)}
                                            />
                                            <span className="text-sm text-gray-700 truncate">{label || "Empty"}</span>
                                        </label>
                                        {typeof count === "number" && (
                                            <span className="text-xs text-gray-500">{count}</span>
                                        )}
                                        {!entry && <span className="text-[10px] uppercase text-yellow-600">Not present in data</span>}
                                    </div>
                                )
                            })}
                            {allowManualOrdering && displayOrder.length > 1 && (
                                <div
                                    className={classNames(
                                        "flex items-center justify-center py-1 text-xs text-gray-500",
                                        dragOverValue === "__END__" ? "bg-ccgreen-50 text-ccgreen-800" : ""
                                    )}
                                    onDragOver={handleDropZoneOver}
                                    onDrop={async (event) => {
                                        event.preventDefault()
                                        await handleDropAtEnd()
                                        handleDropZoneLeave()
                                    }}
                                    onDragLeave={handleDropZoneLeave}
                                >
                                    Drop to move to end
                                </div>
                            )}
                        </div>
                    )}
                    {allowManualOrdering && hasValues && !hasCustomOrder && (
                        <p className="text-xs text-gray-500">Drag values to create a custom axis order.</p>
                    )}
                </div>
            )
        }
        function filterAllocationsPane({ mode, sets, extents, axisOptions, rowFilter, colFilter, viewFilters, totalItems, filterOptions }) {
            const primitiveForFilter = frame

            const addViewFilter = (item) => {
                const axis = axisOptions[item]
                if (axis) {
                    const localFilters = primitiveForFilter.referenceParameters?.explore?.filters ?? []
                    const track = (primitiveForFilter.referenceParameters?.explore?.filterTrack ?? 0) + 1
                    const newFilter = {
                        track: track,
                        sourcePrimId: axis.primitiveId,
                        type: axis.type,
                        subtype: axis.subtype,
                        parameter: axis.parameter,
                        relationship: axis.relationship,
                        treatment: mode,
                        access: axis.access,
                        value: undefined
                    }
                    if (axis.type === "metric_fields") {
                        newFilter.fields = (axis.fields ?? []).map(field => ({ ...field }))
                        if (axis.aggregate) {
                            newFilter.aggregate = axis.aggregate
                        }
                    }
                    localFilters.push(newFilter)
                    primitiveForFilter.setField("referenceParameters.explore.filters", localFilters)
                    primitiveForFilter.setField("referenceParameters.explore.filterTrack", track)
                    if (props.updateFrameExtents) {
                        props.updateFrameExtents(primitiveForFilter)
                    }
                }
            }
            function updateHideNull(val) {
                primitiveForFilter.setField("referenceParameters.explore.hideNull", val)
                setHideNull(val)
                if (props.updateFrameExtents) {
                    props.updateFrameExtents(primitiveForFilter)
                }
            }

            const isFilter = mode === "filter"
            const isAllocation = !isFilter
            
            let allocationPanels = null
            if (isAllocation && sets?.length) {
                allocationPanels = sets.map((set) => {
                    const axisExtents = extents?.[set.selection] ?? []
                    const filterConfig = primitiveForFilter.referenceParameters?.explore?.filters?.[set.mode] ?? {}
                    const allocOrderedValues = CollectionUtils.computeOrderedAxisEntries(axisExtents, filterConfig?.order)
                    return (
                        <Panel collapsable noSpace key={`alloc-${set.selection}`} title={set.title}
                            deleteButton={
                                set.deleteIdx === undefined
                                    ? undefined
                                    : (e) => {
                                        e.preventDefault()
                                        mainstore.promptDelete({
                                            message: "Remove pivot?",
                                            handleDelete: () => {
                                                filterOptions.deleteViewFilter(set.deleteIdx)
                                                return true
                                            }
                                        })
                                    }
                            }
                        >
                            <AxisSelectionList
                                axisName={set.selection}
                                axisTitle={set.title}
                                axisExtents={axisExtents}
                                axisConfig={filterConfig}
                                filterState={set.list}
                                orderedAxisValues={allocOrderedValues}
                                axisPath={`referenceParameters.explore.filters.${set.mode}`}
                                onSelectAll={() => filterOptions.updateAxisFilter(false, set.mode, true, axisExtents)}
                                onClearAll={() => filterOptions.updateAxisFilter(true, set.mode, true, axisExtents)}
                                onToggleValue={(value) => filterOptions.updateAxisFilter(value, set.mode, false, axisExtents)}
                            />
                        </Panel>
                    )
                })
            }
            
            const filterList = !isAllocation ? CollectionUtils.buildFilterPane(
                sets,
                extents,
                filterOptions
            ) : null
    return <>
        <p className="text-xs mt-1">Filtered from {totalItems} to {list.length} items</p>
                {isFilter && frame.type === "view" && (
                    <div className="px-1 pb-2 space-y-3">
                        <DebouncedNumberInput
                            label="Item limit"
                            labelPlacement="outside"
                            placeholder="Unlimited"
                            minValue={1}
                    variant="bordered"
                    value={frame.referenceParameters?.limit ?? ""}
                    onValueChange={(value)=>{
                        if( value === "" || value === null || typeof value === "undefined"){
                            if( frame.referenceParameters?.limit !== undefined ){
                                frame.setField("referenceParameters.limit", null)
                            }
                            return
                        }
                        const parsed = Number(value)
                        if( Number.isNaN(parsed) ){
                            return
                        }
                        const next = Math.max(1, Math.floor(parsed))
                        if( frame.referenceParameters?.limit !== next ){
                            frame.setField("referenceParameters.limit", next)
                        }
                            }}
                        />
                        <Switch
                            size="sm"
                            isSelected={frame.referenceParameters?.limitRandom ?? false}
                            onValueChange={(value)=>{
                                frame.setField("referenceParameters.limitRandom", value)
                            }}
                        >
                            Pick random items
                        </Switch>
                    </div>
                )}
        <div className='w-full px-1 py-2 text-lg flex place-items-center justify-between text-gray-600 font-normal'>
            {isFilter && <TooggleButton title='Hide empty rows / columns' enabled={hideNull} setEnabled={updateHideNull} />}
            <HierarchyNavigator noBorder portal icon={isFilter ? <HeroIcon icon='FunnelPlus' className='w-5 h-5 ' /> : <Icon icon="material-symbols:scatter-plot" className='w-5 h-5 ' />} items={CollectionUtils.axisToHierarchy(axisOptions)} flat placement='left-start' action={(d) => addViewFilter(d.id)} dropdownWidth='w-64' className='ml-auto hover:text-ccgreen-800 hover:shadow-md' />
        </div>
                <div className='w-full p-2 text-sm space-y-2 overflow-y-scroll'>
                    {isAllocation ? allocationPanels : filterList}
                </div>
            </>
        }

        function renderTimelineEventControls() {
            if (!isTimelineEventView) {
                return null
            }
            const timelineSettings = frame.referenceParameters?.sections?.timeline_events ?? {};
            const hiddenEventKeys = new Set(
                [timelineSettings.hiddenEventKeys ?? timelineSettings.hiddenEventIds ?? timelineSettings.hidden ?? []]
                    .flat()
                    .filter(Boolean)
                    .map((entry) => String(entry))
            );
            const importantEventKeys = new Set(
                [timelineSettings.importantEventKeys ?? timelineSettings.importantEventIds ?? timelineSettings.important ?? []]
                    .flat()
                    .filter(Boolean)
                    .map((entry) => String(entry))
            );
            return (
                <Panel collapsable noSpace narrow title="Timeline events">
                    <div className="space-y-2 pt-2">
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="light"
                                isDisabled={timelineEventsForControls.length === 0}
                                onPress={() => frame.setField(`referenceParameters.sections.timeline_events.hiddenEventKeys`, [])}
                            >
                                Show all
                            </Button>
                            <Button
                                size="sm"
                                variant="light"
                                isDisabled={timelineEventsForControls.length === 0}
                                onPress={() => frame.setField(
                                    `referenceParameters.sections.timeline_events.hiddenEventKeys`,
                                    timelineEventsForControls.map((eventEntry) => eventEntry.key)
                                )}
                            >
                                Hide all
                            </Button>
                        </div>
                        {timelineEventsForControls.length === 0 ? (
                            <p className="text-xs text-gray-500">No timeline events found in current view items.</p>
                        ) : (
                            <ScrollShadow className="max-h-64 pr-1">
                                <div className="space-y-2">
                                    {timelineEventsForControls.map((eventEntry) => {
                                        const eventKey = String(eventEntry.key);
                                        const isVisible = !hiddenEventKeys.has(eventKey);
                                        const isImportant = importantEventKeys.has(eventKey);
                                        return (
                                            <div key={eventKey} className="rounded border border-default-200 px-2 py-2 space-y-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <Checkbox
                                                        size="sm"
                                                        isSelected={isVisible}
                                                        onValueChange={(value) => {
                                                            const next = new Set(hiddenEventKeys);
                                                            if (value) {
                                                                next.delete(eventKey);
                                                            } else {
                                                                next.add(eventKey);
                                                            }
                                                            frame.setField(`referenceParameters.sections.timeline_events.hiddenEventKeys`, Array.from(next));
                                                        }}
                                                    >
                                                        Show
                                                    </Checkbox>
                                                    <Checkbox
                                                        size="sm"
                                                        isSelected={isImportant}
                                                        onValueChange={(value) => {
                                                            const next = new Set(importantEventKeys);
                                                            if (value) {
                                                                next.add(eventKey);
                                                            } else {
                                                                next.delete(eventKey);
                                                            }
                                                            frame.setField(`referenceParameters.sections.timeline_events.importantEventKeys`, Array.from(next));
                                                        }}
                                                    >
                                                        Important
                                                    </Checkbox>
                                                </div>
                                                <p className="text-xs font-medium text-gray-700">{eventEntry.event}</p>
                                                <p className="text-[11px] text-gray-500">{eventEntry.date || "Undated"}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollShadow>
                        )}
                    </div>
                </Panel>
            )
        }

        function viewConfigPanel() {
            const candidateDatasets = datasetCandidates.datasets ?? []
            const candidateTimelines = datasetCandidates.timelines ?? []
            const configuredDatasetFields = (datasetViewConfig?.datasets ?? []).map(entry=>typeof entry === "string" ? entry : entry?.field).filter(Boolean)
            const configuredTimelineField = datasetViewConfig?.timelineField
            const existingSet = new Set(candidateDatasets.map(entry=>entry.id))
            const fallbackDatasets = (datasetViewConfig?.datasets ?? [])
                .map(entry=>typeof entry === "string" ? {id: entry, label: entry} : (entry?.field ? {id: entry.field, label: entry.label ?? entry.field} : null))
                .filter(entry=>entry && !existingSet.has(entry.id))
            const datasetOptions = [...candidateDatasets, ...fallbackDatasets]
            const timelineOptions = candidateTimelines.length > 0 ? candidateTimelines : (configuredTimelineField ? [{id: configuredTimelineField, label: configuredTimelineField}] : [])
            const selectedDatasetKeys = configuredDatasetFields
            const datasetOptionMap = new Map(datasetOptions.map(entry=>[entry.id, entry]))
            const showDatasetPanel = frame?.type === "view"
            return <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 font-medium text-gray-500 dark:text-slate-300 px-3 p-2">
                {showDatasetPanel && <Panel collapsable noSpace title="Dataset view" icon={<FontAwesomeIcon icon={["fas", "database"]} />}>
                    <div className="p-2 text-sm space-y-8 font-normal">
                        <Switch size="sm" isSelected={datasetViewEnabled} onValueChange={(value)=>{
                            const next = {
                                ...datasetViewConfig,
                                enabled: value
                            }
                            if( value ){
                                if( next.dateField === undefined ){next.dateField = "point_in_time_iso"}
                                if( next.valueField === undefined ){next.valueField = "value"}
                                if( next.currencyField === undefined ){next.currencyField = "currency"}
                                if( next.normalize === undefined ){next.normalize = {absolute: true}}
                                if( next.seriesPolicy === undefined ){next.seriesPolicy = "split_by_currency"}
                                if( next.sourceSeriesPolicy === undefined ){next.sourceSeriesPolicy = "aggregate"}
                                if( next.categoricalPolicy === undefined ){next.categoricalPolicy = "row_column_split"}
                                if( next.activeMetric === undefined ){next.activeMetric = "datasets"}
                            }
                            updateDatasetView(next)
                        }}>Dataset view</Switch>
                        {datasetViewEnabled && <>
                            {datasetSourcePrimitive?.flowElement && (datasetSourcePrimitive?.primitives?.config?.allItems?.length ?? 0) > 0 && (
                                <Select
                                    className="w-full"
                                    label="Source instance"
                                    variant="bordered"
                                    labelPlacement="outside"
                                    selectedKeys={datasetSourceInstance?.id ? [datasetSourceInstance.id] : []}
                                    onSelectionChange={(value)=>{
                                        const next = Array.from(value)[0]
                                        updateDatasetView({sourceInstanceId: next || null})
                                    }}
                                >
                                    {(datasetSourcePrimitive.primitives.config.allItems ?? []).map(entry=>(
                                        <SelectItem key={entry.id} textValue={entry.title ?? entry.id}>
                                            {entry.title ?? entry.id}
                                        </SelectItem>
                                    ))}
                                </Select>
                            )}
                            <Select
                                className="w-full"
                                label="Datasets"
                                variant="bordered"
                                labelPlacement="outside"
                                selectionMode="multiple"
                                selectedKeys={selectedDatasetKeys}
                                onSelectionChange={(value)=>{
                                    const selections = Array.from(value)
                                    const nextDatasets = selections.map(id=>{
                                        const opt = datasetOptionMap.get(id)
                                        return opt ? {field: opt.id, label: opt.label, seriesKey: opt.label} : {field: id, label: id, seriesKey: id}
                                    })
                                    const next = {datasets: nextDatasets}
                                    if( nextDatasets.length > 0 ){
                                        next.activeMetric = "datasets"
                                    }else if( datasetViewConfig?.activeMetric === "datasets" ){
                                        next.activeMetric = null
                                    }
                                    updateDatasetView(next)
                                }}
                            >
                                {datasetOptions.map(entry=>(
                                    <SelectItem key={entry.id} textValue={entry.label}>{entry.label}</SelectItem>
                                ))}
                            </Select>
                            <Select
                                className="w-full"
                                label="Active metric"
                                variant="bordered"
                                labelPlacement="outside"
                                selectedKeys={[datasetViewConfig?.activeMetric ?? "datasets"]}
                                onSelectionChange={(value)=>{
                                    const next = Array.from(value)[0] ?? "datasets"
                                    updateDatasetView({activeMetric: next})
                                }}
                            >
                                <SelectItem key="datasets" textValue="Datasets">Datasets</SelectItem>
                                <SelectItem key="timeline" textValue="Timeline">Timeline</SelectItem>
                            </Select>
                            <Select
                                className="w-full"
                                label="Timeline dataset"
                                variant="bordered"
                                labelPlacement="outside"
                                selectedKeys={datasetViewConfig?.timelineField ? [datasetViewConfig.timelineField] : []}
                                onSelectionChange={(value)=>{
                                    const next = Array.from(value)[0]
                                    const update = {timelineField: next || null}
                                    if( next ){
                                        update.activeMetric = "timeline"
                                    }else if( datasetViewConfig?.activeMetric === "timeline" ){
                                        update.activeMetric = null
                                    }
                                    updateDatasetView(update)
                                }}
                            >
                                {timelineOptions.map(entry=>(
                                    <SelectItem key={entry.id} textValue={entry.label ?? entry.id}>{entry.label ?? entry.id}</SelectItem>
                                ))}
                            </Select>
                            <DebouncedInput
                                className="w-full"
                                label="Date field"
                                labelPlacement="outside"
                                value={datasetViewConfig?.dateField ?? "point_in_time_iso"}
                                onChange={(value)=>updateDatasetView({dateField: value})}
                            />
                            <DebouncedInput
                                className="w-full"
                                label="Value field"
                                labelPlacement="outside"
                                value={datasetViewConfig?.valueField ?? "value"}
                                onChange={(value)=>updateDatasetView({valueField: value})}
                            />
                            <DebouncedInput
                                className="w-full"
                                label="Currency field"
                                labelPlacement="outside"
                                value={datasetViewConfig?.currencyField ?? "currency"}
                                onChange={(value)=>updateDatasetView({currencyField: value})}
                            />
                            <Switch size="sm" isSelected={datasetViewConfig?.normalize?.absolute !== false} onValueChange={(value)=>{
                                updateDatasetView({normalize: {...(datasetViewConfig?.normalize ?? {}), absolute: value}})
                            }}>Normalize values</Switch>
                            <Select
                                className="w-full"
                                label="Series policy"
                                variant="bordered"
                                labelPlacement="outside"
                                selectedKeys={[datasetViewConfig?.seriesPolicy ?? "split_by_currency"]}
                                onSelectionChange={(value)=>{
                                    const next = Array.from(value)[0]
                                    updateDatasetView({seriesPolicy: next})
                                }}
                            >
                                <SelectItem key="split_by_currency" textValue="Split by currency">Split by currency</SelectItem>
                            </Select>
                            <Select
                                className="w-full"
                                label="Source series policy"
                                variant="bordered"
                                labelPlacement="outside"
                                selectedKeys={[datasetViewConfig?.sourceSeriesPolicy ?? "aggregate"]}
                                onSelectionChange={(value)=>{
                                    const next = Array.from(value)[0]
                                    updateDatasetView({sourceSeriesPolicy: next})
                                }}
                            >
                                <SelectItem key="aggregate" textValue="Aggregate items">Aggregate items</SelectItem>
                                <SelectItem key="split_by_source_item" textValue="Split by source item">Split by source item</SelectItem>
                            </Select>
                            <Select
                                className="w-full"
                                label="Categorical policy"
                                variant="bordered"
                                labelPlacement="outside"
                                selectedKeys={[datasetViewConfig?.categoricalPolicy ?? "row_column_split"]}
                                onSelectionChange={(value)=>{
                                    const next = Array.from(value)[0]
                                    updateDatasetView({categoricalPolicy: next})
                                }}
                            >
                                <SelectItem key="row_column_split" textValue="Row/column split">Row/column split</SelectItem>
                            </Select>
                        </>}
                    </div>
                </Panel>}
                <Panel collapsable noSpace title="View configuration" icon={<FontAwesomeIcon icon={["fal", "tags"]} />}>
                    <div className="p-2 text-sm space-y-2 font-normal">
                        <Select
                            label="View Mode"
                            selectedKeys={[String(activeViewKey ?? viewModeOptions[0]?.id)]}
                            onChange={(e) => updateViewMode(e.target.value)}
                        >
                            {viewModeOptions.map((opt) => (
                                <SelectItem key={String(opt.id)} textValue={opt.title}>
                                    {opt.title}
                                </SelectItem>
                            ))}
                        </Select>
                        <div className='w-full flex'>
                        <ScrollShadow className='w-full overflow-y-scroll space-y-8 max-h-[50vh] h-full flex flex-col'>
                            {viewConfig && (!viewConfig.config || viewConfig.config.length === 0) && <p className='text-sm text-gray-500 text-center'>No settings</p>}
                            {viewConfig && viewConfig.config && Object.keys(viewConfig.config).map(d => {
                                const currentValue = frame.renderConfig?.hasOwnProperty(d) ? frame.renderConfig[d] : viewConfig.config[d].default
                                const onValueChange = async (v) => { await frame.setField(`renderConfig.${d}`, v); updateFrame() }
                                switch (viewConfig.config[d].type) {
                                    case "option_list":
                                        return <Select 
                                                    key={d} 
                                                    label={viewConfig.config[d].title}
                                                    variant="bordered"
                                                    labelPlacement="outside"
                                                    disallowEmptySelection={true}
                                                    selectedKeys={[frame.renderConfig?.[d]].filter(Boolean).map(d=>`${d}`)}
                                                    onSelectionChange={(v)=>{
                                                        v = Array.from(v)[0]
                                                        if( v === "false"){
                                                            v = false
                                                        }else if( v == "true" ){
                                                            v = true
                                                        }
                                                        onValueChange(v)
                                                    }}
                                                    >
                                                         {viewConfig.config[d].options.map(n => (
                                                                <SelectItem key={n.id} textValue={n.title}>{n.title}</SelectItem>
                                                            ))}
                                                    </Select>
                                    case "boolean":
                                        return <Switch size="sm" key={d} isSelected={currentValue} onValueChange={onValueChange}>{viewConfig.config[d].title}</Switch>
                                    case "axis_text":
                                        const mapSize = [0.1, 0.2, 0.5, 0.65, 1, 1.3, 1.5, 1.8, 2, 2.5, 3]
                                        const findScaleIndex = (scale) => {
                                            if (typeof scale !== "number" || !Number.isFinite(scale)) {
                                                return 4
                                            }
                                            const idx = mapSize.findIndex(value => Math.abs(value - scale) < 0.0001)
                                            return idx === -1 ? 4 : idx
                                        }
                                        const defaultScale = frame.renderConfig?.axis_text ?? 1
                                        const hasColumnOverride = frame.renderConfig?.axis_text_columns !== null && frame.renderConfig?.axis_text_columns !== undefined
                                        const hasRowOverride = frame.renderConfig?.axis_text_rows !== null && frame.renderConfig?.axis_text_rows !== undefined
                                        const axisIndependent = frame.renderConfig?.axis_text_independent ?? (hasColumnOverride || hasRowOverride)
                                        const columnScale = hasColumnOverride ? frame.renderConfig?.axis_text_columns : defaultScale
                                        const rowScale = hasRowOverride ? frame.renderConfig?.axis_text_rows : defaultScale
                                        const renderSlider = ({ value, label, onChange }) => (
                                            <div className="flex my-5">
                                                <DebouncedSlider
                                                    className="w-full"
                                                    value={value}
                                                    color="primary"
                                                    label={label}
                                                    maxValue={10}
                                                    minValue={0}
                                                    showSteps={true}
                                                    hideValue={true}
                                                    size="md"
                                                    step={1}
                                                    marks={[
                                                        { value: 0, label: "Tiny" },
                                                        { value: 2, label: "Small" },
                                                        { value: 4, label: "Normal" },
                                                        { value: 6, label: "Medium" },
                                                        { value: 8, label: "Large" },
                                                        { value: 10, label: "Huge" },
                                                    ]}
                                                    onChange={(v) => {
                                                        if (typeof v !== "number") {
                                                            return
                                                        }
                                                        const mapped = mapSize[v]
                                                        if (mapped !== undefined) {
                                                            onChange(mapped)
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )
                                        const setRenderConfigValue = async (key, value) => {
                                            await frame.setField(`renderConfig.${key}`, value)
                                            updateFrame()
                                        }
                                        const handleIndependentToggle = async (value) => {
                                            if (value) {
                                                await frame.setField("renderConfig.axis_text_independent", true)
                                            } else {
                                                const fallback = hasColumnOverride ? columnScale : hasRowOverride ? rowScale : defaultScale
                                                await frame.setField("renderConfig.axis_text_independent", false)
                                                await frame.setField("renderConfig.axis_text", fallback)
                                                await frame.setField("renderConfig.axis_text_columns", null)
                                                await frame.setField("renderConfig.axis_text_rows", null)
                                            }
                                            updateFrame()
                                        }
                                        const handleSingleSliderChange = (mapped) => {
                                            if (mapped !== undefined && frame.renderConfig?.axis_text !== mapped) {
                                                return onValueChange(mapped)
                                            }
                                        }
                                        const handleColumnSlider = (mapped) => {
                                            if (mapped !== undefined && frame.renderConfig?.axis_text_columns !== mapped) {
                                                return setRenderConfigValue("axis_text_columns", mapped)
                                            }
                                        }
                                        const handleRowSlider = (mapped) => {
                                            if (mapped !== undefined && frame.renderConfig?.axis_text_rows !== mapped) {
                                                return setRenderConfigValue("axis_text_rows", mapped)
                                            }
                                        }
                                        return (
                                            <div className="space-y-3">
                                                <Switch size="sm" isSelected={axisIndependent} onValueChange={handleIndependentToggle}>
                                                    Adjust row & column headers separately
                                                </Switch>
                                                {axisIndependent
                                                    ? <>
                                                        {renderSlider({
                                                            value: findScaleIndex(columnScale),
                                                            label: "Column header size",
                                                            onChange: handleColumnSlider
                                                        })}
                                                        {renderSlider({
                                                            value: findScaleIndex(rowScale),
                                                            label: "Row header size",
                                                            onChange: handleRowSlider
                                                        })}
                                                    </>
                                                    : renderSlider({
                                                        value: findScaleIndex(defaultScale),
                                                        label: "Header size",
                                                        onChange: handleSingleSliderChange
                                                    })}
                                            </div>
                                        )
                                    case "font_size":
                                        return <DebouncedNumberInput
                                            label={viewConfig.config[d].title ?? "Font size"}
                                            variant="bordered"
                                            labelPlacement="outside"
                                            minValue={viewConfig.config[d].min ?? 1}
                                            maxValue={viewConfig.config[d].max ?? 144}
                                            value={currentValue}
                                            onValueChange={(v)=>{
                                                return onValueChange(v)
                                            }}
                                        />
                                    case "column_count":
                                        return <DebouncedNumberInput
                                            label="Columns"
                                            variant="bordered"
                                            labelPlacement="outside"
                                            minValue={viewConfig.config[d].min ?? 1}
                                            maxValue={viewConfig.config[d].max ?? 10}
                                            value={currentValue}
                                            onValueChange={(v)=>{
                                                if( v === currentValue ){
                                                    console.log("skip")
                                                    return
                                                }
                                                if( viewConfig.config[d].rebalance_width ){
                                                    const frameDetail = frame.origin?.frames?.[frame.id]
                                                    if( frameDetail?.width ){
                                                        const newWidth = frameDetail.width * currentValue / v
                                                        console.log(`${frameDetail.width} > ${newWidth}`)
                                                        frame.origin.setField(`frames.${frame.id}.width`, newWidth)
                                                    }
                                                }
                                                return onValueChange(v)
                                            }}
                                            startContent={
                                                <div className="pointer-events-none flex items-center">
                                                    <Icon icon='material-symbols:view-column-outline' className="w-6 h-6" />
                                                </div>
                                            }
                                        />
                                    case "string":
                                        return <DebouncedInput
                                            key={d}
                                            label={viewConfig.config[d].title}
                                            variant="bordered"
                                            labelPlacement="outside"
                                            value={currentValue ?? ""}
                                            placeholder={viewConfig.config[d].placeholder ?? ""}
                                            onChange={(v)=>{
                                                return onValueChange(v ?? "")
                                            }}
                                        />
                                }
                            })}
                        </ScrollShadow>
                        </div>
                        {renderTimelineEventControls()}
                        {(frame.type === "query" || frame.type === "view") && <Switch size="sm" key="show_axis_pins" isSelected={frame.renderConfig?.showAxisPin} onValueChange={(v) => { frame.setField('renderConfig.showAxisPin', v); updateFrame() }}>Show axis pins</Switch>}
                    </div>
                </Panel>
            </div>
        }
        function categoryPanel() {
            return <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-300 font-medium px-3 p-2">
                <Panel collapsable noSpace title="Categories" icon={<FontAwesomeIcon icon={["fal", "tags"]} />}>
                    <PrimitiveCard.Categories primitive={frame} scope={filters ? list.map(d => d.id) : undefined} /*directOnly*/ hidePanel className='pb-2 w-full h-fit' />
                    <div type="button"
                        className="flex my-2 font-medium grow-0 bg-white dark:bg-slate-950 hover:bg-gray-100 hover:shadow-sm hover:text-gray-600 justify-center ml-2 p-1 rounded-full shrink-0 text-xs text-gray-400 "
                        onClick={() => addCategory(frame)}>
                        <PlusIcon className="w-5 h-5" />
                    </div>
                    {false && null}
                </Panel>
            </div>
        }
        function filterAndAllocationsPanels() {
            if (!shouldComputeCollectionList) {
                return null
            }
            const shouldRenderFilterPanels = isPivotsPanelOpen || isFiltersPanelOpen || isAxisPanelOpen
            const cachedPanels = shouldRenderFilterPanels ? getFilterPanels() : null
            const resolvePanels = (forceRender = false) => {
                if (cachedPanels) {
                    return cachedPanels
                }
                if (!forceRender) {
                    return null
                }
                return getFilterPanels()
            }
            const renderAllocationsPanel = (forceRender = false) => {
                const panels = resolvePanels(forceRender)
                if (!panels) {
                    return null
                }
                return panels.allocations
            }
            const renderFilterPanel = (forceRender = false) => {
                const panels = resolvePanels(forceRender)
                if (!panels) {
                    return null
                }
                return panels.filters
            }
            const renderAxisPanel = (forceRender = false) => {
                const panels = resolvePanels(forceRender)
                if (!panels) {
                    return null
                }
                return panels.axis
            }
            return <>
                <div className="space-y-2">
                    <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-300 font-medium px-3 p-2">
                        <Panel collapsable noSpace title="Axis" icon={<Icon icon="mdi:axis-arrow-info" className="w-5 h-5" />} onOpenChange={setIsAxisPanelOpen}>
                            {(open) => renderAxisPanel(open)}
                        </Panel>
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-300 font-medium px-3 p-2">
                        <Panel collapsable noSpace title="Pivots" icon={<FontAwesomeIcon icon={["fal", "filter"]} />} onOpenChange={setIsPivotsPanelOpen}>
                            {(open) => renderAllocationsPanel(open)}
                        </Panel>
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-300 font-medium px-3 p-2">
                        <Panel collapsable noSpace title="Filters" icon={<FontAwesomeIcon icon={["fal", "filter"]} />} onOpenChange={setIsFiltersPanelOpen}>
                            {(open) => renderFilterPanel(open)}
                        </Panel>
                    </div>
                </div>
            </>
        }
        function pinSet(title, target) {
            const pins = frame.referenceParameters?.[target] ?? {}
            const pinIds = Object.keys(pins).filter((pinId)=>{
                if(target !== "inputPins"){
                    return true
                }
                return !pinId.startsWith("relay-")
            })
            const showDescriptor = frame.type === "flow" || frame.type === "page"

            function toggleType(pinId, type) {
                let types = (pins[pinId].types ?? [])
                if (types.includes(type)) {
                    types = types.filter(d => d !== type)
                } else {
                    types.push(type)
                }
                frame.setField(`referenceParameters.${target}.${pinId}.types`, types)
            }
            async function removeTargetPin(pinId){
                if(!frame){
                    return
                }
                const pinName = pins[pinId]?.name ?? pinId
                let relationships = []
                if(target === "outputPins"){
                    const map = PrimitiveConfig.getOutputMap(frame) ?? []
                    relationships = map.filter(d=>d.outputPin === pinId).map(d=>({
                        targetId: d.targetId,
                        targetPin: d.targetPin
                    }))
                }else if(target === "inputPins" || target === "controlPins"){
                    const map = PrimitiveConfig.getInputMap(frame) ?? []
                    relationships = map.filter(d=>d.inputPin === pinId).map(d=>({
                        sourceId: d.sourceId,
                        sourcePin: d.sourcePin
                    }))
                }
                const executeRemoval = async ()=>{
                    if(target === "outputPins"){
                        await detachOutputRelationships(pinId)
                    }else if(target === "inputPins" || target === "controlPins"){
                        for(const rel of relationships){
                            const sourcePrim = mainstore.primitive(rel.sourceId)
                            if(sourcePrim){
                                await sourcePrim.removeRelationship(frame, `inputs.${rel.sourcePin}_${pinId}`)
                            }
                        }
                    }
                    const updatedPins = {...pins}
                    delete updatedPins[pinId]
                    await frame.setField(`referenceParameters.${target}`, updatedPins)
                }
                const relationshipCount = relationships.length
                if(relationshipCount > 0){
                    mainstore.promptDelete({
                        prompt: `Remove pin "${pinName}" and disconnect ${relationshipCount} relationship${relationshipCount === 1 ? "" : "s"}?`,
                        handleDelete: async ()=>{
                            await executeRemoval()
                            return true
                        }
                    })
                }else{
                    await executeRemoval()
                }
            }
            return <Panel collapsable noSpace title={title} >
                <div className="px-2 py-2 flex flex-col space-y-2 text-gray-500 @container">
                    <DescriptionList inContainer={true}>
                        {pinIds.map(pinId => (<>
                            <DescriptionTerm inContainer={true}>
                                <div className="flex items-center gap-2">
                                    <DebouncedInput
                                        size="sm"
                                        inline
                                        value={pins[pinId].name}
                                        className="w-full"
                                        classNames={{ input: "!p-0 !bg-transparent" }}
                                        onChange={(value) => {
                                            if (value) {
                                                frame.setField(`referenceParameters.${target}.${pinId}.name`, value)
                                            }
                                        }}
                                    />
                                    <Button size="sm" variant="light" className="ml-auto" onPress={async ()=>{ await removeTargetPin(pinId)}}>
                                        <TrashIcon className="w-4 h-4" />
                                    </Button>
                                </div>
                            </DescriptionTerm>
                            <DescriptionDetails inContainer={true}>
                                <DescriptionList inContainer={true}>
                                    {showDescriptor && <>
                                        <DescriptionTerm inContainer={true}>Description</DescriptionTerm>
                                        <DescriptionDetails inContainer={true}>
                                            <DebouncedInput
                                                size="sm"
                                                inline
                                                value={pins[pinId].description}
                                                className="w-full"
                                                classNames={{ input: "!p-0 !bg-transparent" }}
                                                onChange={(value) => {
                                                    if (value) {
                                                        frame.setField(`referenceParameters.${target}.${pinId}.description`, value)
                                                    }
                                                }}
                                            />
                                        </DescriptionDetails>
                                    </>}
                                    <DescriptionTerm inContainer={true}>Types</DescriptionTerm>
                                    <DescriptionDetails inContainer={true}>
                                        <CheckboxField>
                                            <LegacyCheckbox
                                                onClick={() => toggleType(pinId, "primitive")}
                                                checked={(pins[pinId].types ?? []).includes("primitive")}
                                            />
                                            <Label>Primitive</Label>
                                        </CheckboxField>
                                        <CheckboxField>
                                            <LegacyCheckbox
                                                onClick={() => toggleType(pinId, "string")}
                                                checked={(pins[pinId].types ?? []).includes("string")}
                                            />
                                            <Label>Text</Label>
                                        </CheckboxField>
                                        <CheckboxField>
                                            <LegacyCheckbox
                                                onClick={() => toggleType(pinId, "string_list")}
                                                checked={(pins[pinId].types ?? []).includes("string_list")}
                                            />
                                            <Label>Comma separated list</Label>
                                        </CheckboxField>
                                        <CheckboxField>
                                            <LegacyCheckbox
                                                onClick={() => toggleType(pinId, "anchored")}
                                                checked={(pins[pinId].types ?? []).includes("anchored")}
                                            />
                                            <Label>Anchored</Label>
                                        </CheckboxField>

                                    </DescriptionDetails>
                                </DescriptionList>
                            </DescriptionDetails>
                        </>
                        ))}
                        <DescriptionTerm inContainer={true}></DescriptionTerm>
                        <DescriptionDetails inContainer={true} >
                            <Button variant="bordered" className="w-full" onPress={() => {
                                const pre = `${target}-`
                                const existing = Math.max(0, ...pinIds.filter(d => d.startsWith(pre)).map(d => d.slice(pre.length)))
                                let name = `${pre}${existing + 1}`
                                frame.setField(`referenceParameters.${target}.${name}`, { name: `New ${title}`, types: ["string"] })

                            }}>
                                New Pin
                            </Button>
                        </DescriptionDetails>
                    </DescriptionList>
                </div>
            </Panel>

        }
        function flowControlPanel(){
            if(frame.type !== "flowcontrol"){
                return null
            }
            const flowControl = frame.referenceParameters?.flowControl ?? {}
            const states = flowControl.states ?? []
            const connectionOptions = buildFlowControlConnections()
            const canAddCondition = connectionOptions.length > 0
            const renderCondition = (state, condition)=>{
                const conditionId = condition.id ?? `${state.id}-cond`
                const connectionKey = `${condition.inputPin ?? ""}`
                const selectedComparator = condition.comparator ?? "equals"
                const comparatorOptions = comparatorOptionsForMetric(condition.metric)
                const selectedMetric = condition.metric ?? "count"
                const connection = connectionOptions.find(opt=>opt.key === connectionKey)
                const parameterOptions = buildParameterOptions(connection?.source)
                const renderValueInput = ()=>{
                    if(selectedMetric === "count"){
                        return (
                            <DebouncedInput
                                type="number"
                                className="w-28"
                                label="Value"
                                size="sm"
                                variant="bordered"
                                value={String(condition.value ?? 0)}
                                onChange={async (val)=>{
                                    const parsed = Number(val)
                                    await updateFlowControlCondition(state.id, conditionId, draft=>{
                                        draft.value = Number.isFinite(parsed) ? parsed : 0
                                    })
                                }}
                                debounce={300}
                            />
                        )
                    }
                    if(selectedMetric === "error"){
                        return (
                            <Select
                                className="w-44"
                                size="sm"
                                variant="bordered"
                                label="Status"
                                selectedKeys={[condition.value ?? "error"]}
                                onChange={async (e)=>{
                                    await updateFlowControlCondition(state.id, conditionId, draft=>{
                                        draft.value = e.target.value
                                    })
                                }}
                            >
                                {errorStatusOptions.map(opt=><SelectItem key={opt.id}>{opt.title}</SelectItem>)}
                            </Select>
                        )
                    }
                    return (
                        <DebouncedInput
                            className="w-56"
                            size="sm"
                            variant="bordered"
                            label="Value"
                            value={condition.value ?? ""}
                            onChange={async (val)=>{
                                await updateFlowControlCondition(state.id, conditionId, draft=>{
                                    draft.value = val
                                })
                            }}
                            debounce={300}
                        />
                    )
                }
                return (
                    <div key={conditionId} className="border rounded-md p-2 bg-slate-50 space-y-2">
                        <div className="flex flex-wrap gap-2 items-center">
                            <Select
                                size="sm"
                                variant="bordered"
                                className="min-w-[10rem]"
                                label="Source"
                                selectedKeys={[connectionKey]}
                                onChange={async (e)=>{
                                    const value = e.target.value
                                    const inputPin = value
                                    await updateFlowControlCondition(state.id, conditionId, draft=>{
                                        draft.inputPin = inputPin || undefined
                                        if(draft.sourcePin){
                                            delete draft.sourcePin
                                        }
                                    })
                                }}
                            >
                                        {connectionOptions.map(opt=>(
                                    <SelectItem key={opt.key}>{opt.label}</SelectItem>
                                ))}
                            </Select>
                            <Select
                                size="sm"
                                variant="bordered"
                                className="w-40"
                                label="Metric"
                                selectedKeys={[selectedMetric]}
                                onChange={async (e)=>{
                                    const metric = e.target.value
                                    await updateFlowControlCondition(state.id, conditionId, draft=>{
                                        draft.metric = metric
                                        if(metric === "count"){
                                            draft.comparator = "gte"
                                            const numericValue = Number(draft.value ?? 0)
                                            draft.value = Number.isFinite(numericValue) ? numericValue : 0
                                        }else if(metric === "error"){
                                            draft.comparator = "equals"
                                            draft.value = draft.value ?? "error"
                                        }else{
                                            draft.comparator = draft.comparator ?? "equals"
                                            draft.value = draft.value ?? ""
                                        }
                                    })
                                }}
                            >
                                <SelectItem key="title">Title</SelectItem>
                                <SelectItem key="parameter">Parameter</SelectItem>
                                <SelectItem key="count">Count</SelectItem>
                                <SelectItem key="error">Error status</SelectItem>
                            </Select>
                            <Select
                                size="sm"
                                variant="bordered"
                                className="w-36"
                                label="Comparator"
                                selectedKeys={[selectedComparator]}
                                onChange={async (e)=>{
                                    await updateFlowControlCondition(state.id, conditionId, draft=>{
                                        draft.comparator = e.target.value
                                    })
                                }}
                            >
                                {comparatorOptions.map(opt=>(
                                    <SelectItem key={opt.id}>{opt.title}</SelectItem>
                                ))}
                            </Select>
                            {renderValueInput()}
                            <Button
                                size="sm"
                                variant="light"
                                className="ml-auto"
                                onPress={async ()=>{ await removeFlowControlCondition(state.id, conditionId)}}
                            >
                                <TrashIcon className="w-4 h-4" />
                            </Button>
                        </div>
                        {selectedMetric === "parameter" && (
                            <div className="flex flex-wrap gap-2 items-center">
                                <Select
                                    size="sm"
                                    variant="bordered"
                                    className="w-56"
                                    label="Parameter"
                                    selectedKeys={[condition.parameterPath ?? ""]}
                                    onChange={async (e)=>{
                                        await updateFlowControlCondition(state.id, conditionId, draft=>{
                                            draft.parameterPath = e.target.value
                                        })
                                    }}
                                >
                                    {parameterOptions.map(opt=>(
                                        <SelectItem key={opt.id}>{opt.title}</SelectItem>
                                    ))}
                                </Select>
                                <DebouncedInput
                                    size="sm"
                                    variant="bordered"
                                    className="flex-1"
                                    label="Path"
                                    placeholder="custom.path"
                                    value={condition.parameterPath ?? ""}
                                    onChange={async (val)=>{
                                        await updateFlowControlCondition(state.id, conditionId, draft=>{
                                            draft.parameterPath = val
                                        })
                                    }}
                                    debounce={300}
                                />
                            </div>
                        )}
                    </div>
                )
            }
            return (
                <div className="space-y-3">
                    {pinSet("Inputs", "inputPins")}
                    <Panel collapsable noSpace title="Flow Control">
                        <div className="space-y-3">
                        {states.length === 0 && (
                            <p className="text-sm text-gray-500">
                                No flow control states defined. States evaluate your inputs and expose output pins you can wire to downstream steps.
                            </p>
                        )}
                        {states.map(state=>{
                            const stateId = state.id
                            const activeTypes = Array.isArray(state.types) ? state.types : []
                            const passThroughPins = Array.isArray(state.passThroughPins) ? state.passThroughPins : []
                            return (
                                <div key={stateId} className="border rounded-md bg-white dark:bg-slate-950 p-3 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <DebouncedInput
                                            size="sm"
                                            inline
                                            value={state.name ?? ""}
                                            className="w-full"
                                            classNames={{ input: "!p-0 !bg-transparent text-base font-medium" }}
                                            onChange={async (value)=>{
                                                if(!value){
                                                    return
                                                }
                                                await updateFlowControlState(stateId, draft=>{
                                                    draft.name = value
                                                })
                                            }}
                                        />
                                        <Button size="sm" variant="light" onPress={async ()=>{ await removeFlowControlState(state) }}>
                                            <TrashIcon className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <DebouncedInput
                                        size="sm"
                                        variant="bordered"
                                        label="Description"
                                        value={state.description ?? ""}
                                        onChange={async (val)=>{
                                            await updateFlowControlState(stateId, draft=>{
                                                draft.description = val
                                            })
                                        }}
                                        debounce={400}
                                    />
                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                        <Select
                                            size="sm"
                                            variant="bordered"
                                            className="w-44"
                                            label="When"
                                            selectedKeys={[state.aggregator ?? "all"]}
                                            onChange={async (e)=>{
                                                await updateFlowControlState(stateId, draft=>{
                                                    draft.aggregator = e.target.value === "any" ? "any" : "all"
                                                })
                                            }}
                                        >
                                            <SelectItem key="all">All conditions are true</SelectItem>
                                            <SelectItem key="any">Any condition is true</SelectItem>
                                        </Select>
                                        <Checkbox
                                            size="sm"
                                            isSelected={state.haltFlow === true}
                                            onValueChange={async (selected)=>{
                                                await updateFlowControlState(stateId, draft=>{
                                                    draft.haltFlow = selected
                                                })
                                            }}
                                        >
                                            Halt flow when this state matches
                                        </Checkbox>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs uppercase tracking-wide text-gray-400">Output types</p>
                                        <div className="flex flex-wrap gap-3">
                                            {["primitive","string","string_list","anchored"].map(type=>(
                                                <CheckboxField key={type}>
                                                    <LegacyCheckbox
                                                        onClick={async ()=>{ await toggleFlowControlStateType(stateId, type) }}
                                                        checked={activeTypes.includes(type)}
                                                    />
                                                    <Label>
                                                        {type === "string_list"
                                                            ? "Comma separated"
                                                            : type.charAt(0).toUpperCase() + type.slice(1)}
                                                    </Label>
                                                </CheckboxField>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs uppercase tracking-wide text-gray-400">Pass through inputs</p>
                                        <Select
                                            selectionMode="multiple"
                                            size="sm"
                                            variant="bordered"
                                            className="min-w-[16rem]"
                                            label={connectionOptions.length === 0 ? "Connect inputs to enable" : "Select input pins"}
                                            selectedKeys={new Set(passThroughPins)}
                                            isDisabled={connectionOptions.length === 0}
                                            onSelectionChange={async keys=>{
                                                const selectedPins = Array.from(keys)
                                                    .map(key=>`${key}`)
                                                    .filter(key=>connectionOptions.find(opt=>opt.inputPin === key))
                                                await updateFlowControlConfig(draft=>{
                                                    const target = (draft.states ?? []).find(s=>s.id === stateId)
                                                    if(!target){
                                                        return
                                                    }
                                                    target.passThroughPins = selectedPins
                                                })
                                            }}
                                        >
                                            {connectionOptions.map(opt=>{
                                                if(!opt.inputPin){
                                                    return null
                                                }
                                                return (
                                                    <SelectItem key={opt.inputPin} textValue={opt.label}>
                                                        {opt.label}
                                                    </SelectItem>
                                                )
                                            })}
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-xs uppercase tracking-wide text-gray-400">Conditions</p>
                                        {state.conditions?.map(condition=>renderCondition(state, condition))}
                                        <Button
                                            variant="bordered"
                                            isDisabled={!canAddCondition}
                                            className="mt-1"
                                            onPress={async ()=>{ await addFlowControlCondition(stateId) }}
                                        >
                                            {canAddCondition ? "Add Condition" : "Add inputs to configure conditions"}
                                        </Button>
                                    </div>
                                </div>
                            )
                        })}
                        <Button variant="bordered" onPress={async ()=>{ await addFlowControlState() }}>
                            Add State
                        </Button>
                        </div>
                    </Panel>
                </div>
            )
        }
        async function updateFlowConfigurations(mutator){
            const current = cloneFlowConfigurations(frame.referenceParameters?.configurations ?? {})
            mutator(current)
            await frame.setField("referenceParameters.configurations", current)
        }
        async function addFlowConfiguration(){
            const currentKeys = new Set(Object.keys(frame.referenceParameters?.configurations ?? {}))
            const configKey = makeUniqueFlowConfigToken(currentKeys, "configuration")
            await updateFlowConfigurations(draft=>{
                draft[configKey] = {
                    title: `Configuration ${Object.keys(draft).length + 1}`,
                    description: "",
                    type: "options",
                    can_select_multiple: false,
                    default: "option_1",
                    options: [
                        { id: "option_1", title: "Option 1" }
                    ]
                }
            })
        }
        async function removeFlowConfiguration(configKey){
            await updateFlowConfigurations(draft=>{
                delete draft[configKey]
            })
        }
        async function updateFlowConfigurationField(configKey, field, value){
            await updateFlowConfigurations(draft=>{
                if(!draft[configKey]){
                    return
                }
                draft[configKey][field] = value
            })
        }
        async function addFlowConfigurationOption(configKey){
            await updateFlowConfigurations(draft=>{
                const target = draft[configKey]
                if(!target){
                    return
                }
                const existingIds = new Set((target.options ?? []).map(option=>option?.id).filter(Boolean))
                const optionId = makeUniqueFlowConfigToken(existingIds, "option")
                target.options = [
                    ...(target.options ?? []),
                    {
                        id: optionId,
                        title: `Option ${(target.options ?? []).length + 1}`
                    }
                ]
            })
        }
        async function removeFlowConfigurationOption(configKey, optionIndex){
            await updateFlowConfigurations(draft=>{
                const target = draft[configKey]
                if(!target || !Array.isArray(target.options)){
                    return
                }
                const removedId = target.options[optionIndex]?.id
                target.options = target.options.filter((_, idx)=>idx !== optionIndex)
                if(removedId && target.default === removedId){
                    delete target.default
                }
                if(removedId && Array.isArray(target.defaultValues)){
                    target.defaultValues = target.defaultValues.filter(value=>value !== removedId)
                }
            })
        }
        async function updateFlowConfigurationOption(configKey, optionIndex, field, value){
            await updateFlowConfigurations(draft=>{
                const target = draft[configKey]
                if(!target || !Array.isArray(target.options) || !target.options[optionIndex]){
                    return
                }
                if(field === "id"){
                    const previousId = target.options[optionIndex]?.id
                    const otherIds = new Set(
                        target.options
                            .map((option, idx)=>idx === optionIndex ? undefined : option?.id)
                            .filter(Boolean)
                    )
                    const nextId = makeUniqueFlowConfigToken(otherIds, value || "option")
                    target.options[optionIndex].id = nextId
                    if(target.default === previousId){
                        target.default = nextId
                    }
                    if(Array.isArray(target.defaultValues)){
                        target.defaultValues = target.defaultValues.map(entry=>entry === previousId ? nextId : entry)
                    }
                    return
                }
                target.options[optionIndex][field] = value
            })
        }
        async function submitPublishFlow(){
            await mainstore.doPrimitiveAction(frame, "publish_flow")
        }
        function flowConfigurationsPanel(){
            if(frame.type !== "flow"){
                return null
            }
            const configurations = frame.referenceParameters?.configurations ?? {}
            const entries = Object.entries(configurations)
            return (
                <Panel collapsable noSpace className="border border-gray-200 dark:border-slate-700 rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title="Configurations">
                    <div className="space-y-3 p-2">
                        {entries.length === 0 && (
                            <p className="text-sm text-gray-500">
                                No flow-level configurations yet. Add one to expose options on new flow instances.
                            </p>
                        )}
                        {entries.map(([configKey, config])=>{
                            const options = Array.isArray(config?.options) ? config.options : []
                            const optionIds = options.map(option=>option?.id).filter(Boolean)
                            const optionIdSet = new Set(optionIds)
                            const defaultSingle = optionIdSet.has(config?.default) ? config.default : undefined
                            const defaultMultiple = Array.isArray(config?.defaultValues)
                                ? config.defaultValues.filter(value=>optionIdSet.has(value))
                                : []
                            return (
                                <div key={configKey} className="rounded-md border border-gray-200 bg-white p-3 space-y-3 dark:border-slate-700 dark:bg-slate-950">
                                    <div className="flex items-center gap-2">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{`fc_${configKey}`}</p>
                                        <Button
                                            size="sm"
                                            variant="light"
                                            className="ml-auto"
                                            onPress={async ()=>{ await removeFlowConfiguration(configKey) }}
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <DebouncedInput
                                        size="sm"
                                        variant="bordered"
                                        label="Title"
                                        value={config?.title ?? ""}
                                        onChange={async (value)=>{
                                            await updateFlowConfigurationField(configKey, "title", value)
                                        }}
                                        debounce={300}
                                    />
                                    <DebouncedInput
                                        size="sm"
                                        variant="bordered"
                                        label="Description"
                                        value={config?.description ?? ""}
                                        onChange={async (value)=>{
                                            await updateFlowConfigurationField(configKey, "description", value)
                                        }}
                                        debounce={300}
                                    />
                                    <div className="flex flex-wrap gap-3 items-center">
                                        <Select
                                            size="sm"
                                            variant="bordered"
                                            className="w-48"
                                            label="Type"
                                            selectedKeys={[config?.type ?? "options"]}
                                            isDisabled
                                        >
                                            <SelectItem key="options">Options</SelectItem>
                                        </Select>
                                        <Checkbox
                                            size="sm"
                                            isSelected={config?.can_select_multiple === true}
                                            onValueChange={async (selected)=>{
                                                await updateFlowConfigurations(draft=>{
                                                    const target = draft[configKey]
                                                    if(!target){
                                                        return
                                                    }
                                                    target.can_select_multiple = selected
                                                    if(selected){
                                                        const nextDefaults = [target.defaultValues]
                                                            .flat()
                                                            .map(value=>`${value ?? ""}`.trim())
                                                            .filter(Boolean)
                                                        const fallback = `${target.default ?? ""}`.trim()
                                                        if(fallback && !nextDefaults.includes(fallback)){
                                                            nextDefaults.unshift(fallback)
                                                        }
                                                        target.defaultValues = nextDefaults
                                                        if(target.default !== undefined){
                                                            delete target.default
                                                        }
                                                    }else{
                                                        const fallback = [target.defaultValues]
                                                            .flat()
                                                            .map(value=>`${value ?? ""}`.trim())
                                                            .find(Boolean)
                                                        target.default = fallback || ""
                                                        if(target.defaultValues !== undefined){
                                                            delete target.defaultValues
                                                        }
                                                    }
                                                })
                                            }}
                                        >
                                            Allow multiple selections
                                        </Checkbox>
                                    </div>
                                    <div>
                                        {config?.can_select_multiple === true ? (
                                            <Select
                                                size="sm"
                                                variant="bordered"
                                                label="Default selections"
                                                selectionMode="multiple"
                                                selectedKeys={defaultMultiple}
                                                onChange={async (e)=>{
                                                    const value = e.target.value ?? ""
                                                    const nextValues = value
                                                        .split(",")
                                                        .map(entry=>entry.trim())
                                                        .filter(Boolean)
                                                    await updateFlowConfigurations(draft=>{
                                                        const target = draft[configKey]
                                                        if(!target){
                                                            return
                                                        }
                                                        target.defaultValues = nextValues
                                                        if(target.default !== undefined){
                                                            delete target.default
                                                        }
                                                    })
                                                }}
                                            >
                                                {options.map((option)=>(
                                                    <SelectItem key={option.id}>{option.title ?? option.id}</SelectItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Select
                                                size="sm"
                                                variant="bordered"
                                                label="Default selection"
                                                selectedKeys={defaultSingle ? [defaultSingle] : []}
                                                onChange={async (e)=>{
                                                    const value = `${e.target.value ?? ""}`.trim()
                                                    await updateFlowConfigurations(draft=>{
                                                        const target = draft[configKey]
                                                        if(!target){
                                                            return
                                                        }
                                                        if(value){
                                                            target.default = value
                                                        }else{
                                                            delete target.default
                                                        }
                                                        if(target.defaultValues !== undefined){
                                                            delete target.defaultValues
                                                        }
                                                    })
                                                }}
                                            >
                                                {options.map((option)=>(
                                                    <SelectItem key={option.id}>{option.title ?? option.id}</SelectItem>
                                                ))}
                                            </Select>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-xs uppercase tracking-wide text-gray-400">Options</p>
                                        {options.length === 0 && (
                                            <p className="text-sm text-gray-500">No options yet.</p>
                                        )}
                                        {options.map((option, optionIndex)=>(
                                            <div key={`${configKey}-${optionIndex}`} className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 p-2 dark:border-slate-700 md:grid-cols-[minmax(0,180px)_minmax(0,1fr)_auto]">
                                                <DebouncedInput
                                                    size="sm"
                                                    variant="bordered"
                                                    label="Option ID"
                                                    value={option?.id ?? ""}
                                                    onChange={async (value)=>{
                                                        await updateFlowConfigurationOption(configKey, optionIndex, "id", value)
                                                    }}
                                                    debounce={300}
                                                />
                                                <DebouncedInput
                                                    size="sm"
                                                    variant="bordered"
                                                    label="Option title"
                                                    value={option?.title ?? ""}
                                                    onChange={async (value)=>{
                                                        await updateFlowConfigurationOption(configKey, optionIndex, "title", value)
                                                    }}
                                                    debounce={300}
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="md:self-end"
                                                    onPress={async ()=>{ await removeFlowConfigurationOption(configKey, optionIndex) }}
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button
                                            size="sm"
                                            variant="bordered"
                                            onPress={async ()=>{ await addFlowConfigurationOption(configKey) }}
                                        >
                                            Add Option
                                        </Button>
                                    </div>
                                </div>
                            )
                        })}
                        <Button variant="bordered" onPress={async ()=>{ await addFlowConfiguration() }}>
                            Add Configuration
                        </Button>
                    </div>
                </Panel>
            )
        }
        function extensionsPanel() {
            const extensionsOwner = frame
            if (!extensionsOwner) return null
            const allExts = getAllExtensions()
            if (allExts.length === 0) return null
            const toggles = extensionsOwner.referenceParameters?.extensions ?? {}
            return (
                <Panel collapsable noSpace className="border border-gray-200 dark:border-slate-700 rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title="Extensions">
                    <div className="space-y-3 p-2">
                        <p className="text-xs text-gray-500">
                            Enable UI extensions to show additional fields and actions for items in this {extensionsOwner.type === "flow" ? "flow" : "board"}.
                        </p>
                        {allExts.map((ext) => {
                            const enabled = Boolean(toggles[ext.id])
                            return (
                                <div key={ext.id} className="flex items-center justify-between rounded-md border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-700 dark:text-slate-200">{ext.title}</p>
                                        <p className="text-xs text-gray-500 dark:text-slate-400">{ext.description}</p>
                                    </div>
                                    <Switch
                                        size="sm"
                                        isSelected={enabled}
                                        onValueChange={async (v) => {
                                            await toggleExtension(extensionsOwner, ext.id, v)
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </Panel>
            )
        }
        function flowPanel() {
            let activeFlowInstance, flowInstanceToShow
            if (frame.type === "flow") {
                activeFlowInstance = resolveFlowInstanceSelection(props.flowInstances, frame.referenceParameters?.explore?.view).instance
                //if (frame.flowElement) {
                    flowInstanceToShow = activeFlowInstance
                //}
            }
            const flowPanelParameters = frame.type === "flow"
                ? {
                    ...(frame.metadata?.parameters ?? {}),
                    importFromFIs: {
                        title: "Import from flow insstances",
                        type: "boolean",
                        order: -20
                    },
                    validflow: {
                        title: "Import source flow",
                        type: "workspace_flow_primitive",
                        condition: {
                            importFromFIs: true
                        },
                        order: -19
                    }
                }
                : frame.metadata?.parameters

            return <><div className="space-y-2">
                <Panel collapsable noSpace className="border border-gray-200 dark:border-slate-700 rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`${frame.metadata.title} details`}>
                    <div className="p-2 ">
                        <PrimitiveCard.Parameters
                          primitive={frame}
                          activeParameters={flowPanelParameters}
                          includeTitle
                          editing
                          leftAlign
                          density="sm"
                          className="text-xs text-slate-500"
                          fullList
                        />
                    </div>
                    <div className="px-4 pb-2 space-y-2 text-sm text-gray-600">
                        {pinSet("Inputs", "inputPins")}
                        {pinSet("Outputs", "outputPins")}
                        {pinSet("Control", "controlPins")}
                    </div>
                </Panel>
                {flowConfigurationsPanel()}
            </div>
                {!filters && frame.type === "flow" && isTopLevelFlow && (
                  <Button variant="bordered" onPress={submitPublishFlow}>
                    {frame.published?.version ? `Republish v${Number(frame.published.version) + 1}` : "Publish v1"}
                  </Button>
                )}
                {!filters && (frame.type === "flow" && !frame.flowElement) && (
                  <Button variant="bordered" onPress={() => mainstore.doPrimitiveAction(frame, "create_flowinstance")}>
                    New Instance
                  </Button>
                )}
                {!filters && (frame.type === "flow" && frame.flowElement && props.inFlowInstance.id) && (
                  <Button variant="bordered" onPress={() => mainstore.doPrimitiveAction(props.inFlowInstance, "run_flowinstance_from_step", { from: frame.id, force: true })}>
                    Run flow as step
                  </Button>
                )}
                {!filters && (frame.type === "flow" && frame.flowElement && flowInstanceToShow) && (
                  <Button variant="bordered" onPress={() => mainstore.doPrimitiveAction(flowInstanceToShow, "run_subflow", { subFlowId: frame.id })}>
                    Run subflow
                  </Button>
                )}
                {!filters && (frame.type === "flow" && !frame.flowElement && flowInstanceToShow) && (
                  <Button variant="bordered" onPress={() => mainstore.doPrimitiveAction(flowInstanceToShow, "run_subflow", { subFlowId: frame.id })}>
                    Run instance
                  </Button>
                )}
                {!filters && (frame.type === "flow" && !frame.flowElement ) && (
                  <Button variant="bordered" onPress={() => mainstore.doPrimitiveAction(frame, "run_flow")}>
                    Run flow
                  </Button>
                )}
                {!filters && (frame.type === "flow") && flowInstanceToShow && (
                  <Dropdown>
                    <DropdownTrigger>
                      <Button variant="bordered" endContent={<ChevronDownIcon className="h-4 w-4" />}>
                        Scaffold instance
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Scaffold options">
                      <DropdownItem key="scaffold_current" onPress={() => mainstore.doPrimitiveAction(flowInstanceToShow, "instance_scaffold")}>
                        Scaffold instance
                      </DropdownItem>
                      <DropdownItem key="scaffold_all" onPress={() => {
                        const store = MainStore()
                        for (const fi of (props.flowInstances ?? [])) {
                          store.doPrimitiveAction(fi, "instance_scaffold")
                        }
                      }}>
                        Scaffold all instances
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                )}
                {!filters && (frame.type === "flow") && (
                  <Button variant="bordered" onPress={() => {
                    if (props.inFlowInstance) {
                      mainstore.doPrimitiveAction(frame, "workflow_scaffold", { subFlowForInstanceId: props.inFlowInstance.id })
                    } else {
                      mainstore.doPrimitiveAction(frame, "workflow_scaffold")
                    }
                  }}>
                    Scaffold flow
                  </Button>
                )}
            </>
        }



        content = <>
            <div className="p-3 space-y-4 bg-white dark:bg-slate-950 dark:bg-slate-950/60">
                {activeInfo}
                {frame.type === "summary" && <SummaryCard primitive={frame} />}
                {frame.type === "flowcontrol" && flowControlPanel()}


                {frame.type === "element" && docDslPaginationInfo && (
                    <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title="Document pages" icon={<Icon icon="mdi:file-document-multiple-outline" className="w-5 h-5" />}>
                        <div className="p-2 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <Button
                                    size="sm"
                                    variant="light"
                                    isDisabled={docDslPaginationInfo.currentPage <= 1}
                                    onPress={() => {
                                        const prev = Math.max(1, docDslPaginationInfo.currentPage - 1)
                                        frame.setField("renderConfig._documentPage", prev)
                                    }}
                                >
                                    <ChevronLeftIcon className="w-4 h-4" />
                                    Previous
                                </Button>
                                <span className="text-sm font-medium text-gray-700">
                                    Page {docDslPaginationInfo.currentPage} of {docDslPaginationInfo.totalPages}
                                </span>
                                <Button
                                    size="sm"
                                    variant="light"
                                    isDisabled={docDslPaginationInfo.currentPage >= docDslPaginationInfo.totalPages}
                                    onPress={() => {
                                        const next = Math.min(docDslPaginationInfo.totalPages, docDslPaginationInfo.currentPage + 1)
                                        frame.setField("renderConfig._documentPage", next)
                                    }}
                                >
                                    Next
                                    <ChevronRightIcon className="w-4 h-4" />
                                </Button>
                            </div>
                            <DebouncedNumberInput
                                size="sm"
                                variant="bordered"
                                label="Go to page"
                                minValue={1}
                                maxValue={docDslPaginationInfo.totalPages}
                                value={docDslPaginationInfo.currentPage}
                                onValueChange={(value) => {
                                    const page = Number(value)
                                    if (Number.isFinite(page) && page >= 1 && page <= docDslPaginationInfo.totalPages) {
                                        frame.setField("renderConfig._documentPage", page)
                                    }
                                }}
                            />
                        </div>
                    </Panel>
                )}

                {frame.type === "element" && <div className="space-y-2">
                    <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`${frame.metadata.title} details`}>
                        <div className="p-2 ">
                            <PrimitiveCard.Parameters
                              primitive={frame}
                              items={props.originalList ?? list}
                              editing
                              leftAlign
                              density="sm"
                              className="text-xs text-slate-500"
                              fullList
                            />
                            {props.frameAction && <Button size="sm" variant='ghost' onPress={() => { props.frameAction(frame, "capture_sizing") }}>Capture default sizing</Button>}
                            <div className="py-2 text-gray-500 text-sm">
                                <Checkbox
                                    size="sm"
                                    isSelected={frame.referenceParameters?.dynamicContent}
                                    onValueChange={(v) => { frame.setField(`referenceParameters.dynamicContent`, v) }}
                                >Dynamic presentation content</Checkbox>
                                <Panel collapsable noSpace title="Colors" narrow>
                                    {(() => {
                                        // Color palette selection for element primitives
                                        const isElement = frame.type === "element"
                                        const elementPage = isElement ? frame.origin : underlying
                                        const themeKey = elementPage?.renderConfig?.theme || "default";
                                        const theme = themes[themeKey] || themes.default;
                                        const themePaletterComponents = Object.keys(theme.palette ?? {})
                                        const currentText = frame.referenceParameters?.text_color ?? "primary"
                                        const sectionStyle = frame.renderConfig?.themeStyle ?? "normal"

                                        return (
                                            <div className="space-y-3">
                                                        <Select
                                                                className="w-full"
                                                                label="Style"
                                                                size="sm"
                                                                variant="bordered"
                                                                selectedKeys={[sectionStyle].filter(Boolean)}
                                                                disallowEmptySelection={true}
                                                                onSelectionChange={(v) => {
                                                                    frame.setField(`renderConfig.themeStyle`, Array.from(v)[0])
                                                                }}>
                                                                <SelectItem key="normal">Paragraph</SelectItem>
                                                                <SelectItem key="panel">Call out</SelectItem>
                                                            </Select>
                                                    <div className="flex items-center space-x-2">
                                                        <Select
                                                            className="max-w-[10rem]"
                                                            size="sm"
                                                            label="Text color"
                                                            selectedKeys={currentText.startsWith("#") ? [] : [currentText]}
                                                            onSelectionChange={(v) => {
                                                                const val = Array.from(v)[0];
                                                                if (typeof val === 'string') {
                                                                    frame.setField("referenceParameters.text_color", val)
                                                                }
                                                            }}
                                                        >
                                                            {themePaletterComponents.map(n => (
                                                                <SelectItem key={n}>{n}</SelectItem>
                                                            ))}
                                                        </Select>
                                                        <input
                                                            aria-label="Custom text color"
                                                            type="color"
                                                            className="h-6 w-10 border rounded"
                                                            value={/^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(currentText ?? '') ? currentText : '#ffffff'}
                                                            onChange={(e) => frame.setField('referenceParameters.text_color', e.target.value)}
                                                        />
                                                    </div>

                                                </div>
                                            );
                                        })()}
                                    </Panel>
                                </div>
                                <div className="py-2 text-gray-500 text-sm">
                                    {(() => {
                                        const elementConfig = frame.getConfig ?? {};
                                        if (elementConfig.extract !== "content") {
                                            return null;
                                        }
                                        const columnCount = Number(frame.referenceParameters?.columns ?? elementConfig.columns ?? 1) || 1;
                                        const columnSpacing = frame.referenceParameters?.column_spacing ?? elementConfig.column_spacing;
                                        return (
                                            <Panel collapsable noSpace title="Text layout" narrow>
                                                <div className="space-y-2">
                                                    <DebouncedNumberInput
                                                        label="Columns"
                                                        size="sm"
                                                        variant="bordered"
                                                        minValue={1}
                                                        maxValue={8}
                                                        value={columnCount}
                                                        onValueChange={(value) => {
                                                            const next = Number(value);
                                                            if (!Number.isFinite(next) || next < 1) {
                                                                return frame.setField("referenceParameters.columns", 1);
                                                            }
                                                            return frame.setField("referenceParameters.columns", Math.floor(next));
                                                        }}
                                                    />
                                                    <DebouncedNumberInput
                                                        label="Column spacing"
                                                        size="sm"
                                                        variant="bordered"
                                                        minValue={0}
                                                        maxValue={80}
                                                        value={columnSpacing}
                                                        onValueChange={(value) => {
                                                            const next = Number(value);
                                                            if (!Number.isFinite(next) || next < 0) {
                                                                return frame.setField("referenceParameters.column_spacing", null);
                                                            }
                                                            return frame.setField("referenceParameters.column_spacing", next);
                                                        }}
                                                    />
                                                </div>
                                            </Panel>
                                        );
                                    })()}
                                </div>
                                <div className="py-2 text-gray-500 text-sm">
                                    <Panel collapsable noSpace title="Sections" narrow>
                                        {(() => {
                                            if (list[0]?.type === "summary" || list[0]?.referenceParameters?.structured_summary) {
            const templateInfo = resolveSummaryStructureTemplate(frame);
            const sampleSummary = list.find(item => item.type === "summary" || list[0]?.referenceParameters?.structured_summary);
            let resolvedSectionConfig = frame.referenceParameters?.sections ?? {};
            let sectionOrder = [];
            let preparedSections;
            const sectionKeyAliases = new Map();
            const normalizeSectionKey = (key) => {
                if (!key) {
                    return key;
                }
                return sectionKeyAliases.get(key) ?? key;
            };

            if (templateInfo.template || sampleSummary?.referenceParameters?.structured_summary) {
                const { sections: prepared, sectionConfig: mergedConfig, template } = prepareStructuredSummary({
                    structuredSummary: sampleSummary?.referenceParameters?.structured_summary,
                    sectionConfig: frame.referenceParameters?.sections,
                    summaryFallback: sampleSummary?.referenceParameters?.summary,
                    structureTemplate: templateInfo.template,
                    includeSegmentTitle: false,
                });
                resolvedSectionConfig = mergedConfig ?? {};
                preparedSections = prepared ?? [];
                (preparedSections ?? []).forEach((section) => {
                    const canonicalKey = section.configKey ?? section.originalHeading ?? section.heading;
                    if (!canonicalKey) {
                        return;
                    }
                    [section.heading, section.originalHeading]
                        .filter(Boolean)
                        .forEach((alias) => sectionKeyAliases.set(alias, canonicalKey));
                });
                const templateHeadings = template?.map(entry => entry.heading).filter(Boolean) ?? [];
                sectionOrder = [...templateHeadings].map((heading) => normalizeSectionKey(heading));
                if (preparedSections.length > 0) {
                    sectionOrder.push(
                        ...preparedSections
                            .map(section => normalizeSectionKey(section.configKey ?? section.originalHeading ?? section.heading))
                            .filter(Boolean),
                    );
                }
            } else {
                sectionOrder = list
                    .flatMap(d => Array.isArray(d.referenceParameters?.structured_summary) ? d.referenceParameters.structured_summary.map(entry => normalizeSectionKey(entry.heading)) : [])
                    .filter((heading, index, array) => heading && array.indexOf(heading) === index);
            }

            const configuredKeys = Object.keys(resolvedSectionConfig ?? {}).filter(
                key => key !== "segment_title" && key !== "vertical_sections" && key !== "timeline_events",
            ).map((key) => normalizeSectionKey(key));

            let sections = [...sectionOrder, ...configuredKeys].filter(
                (section, index, array) => section && array.indexOf(section) === index,
            );

            if (sections.length === 0) {
                sections = list
                    .flatMap(d => Array.isArray(d.referenceParameters?.structured_summary) ? d.referenceParameters.structured_summary?.map(entry => normalizeSectionKey(entry.heading)) : [])
                    .filter((heading, index, array) => heading && array.indexOf(heading) === index);
            }

            const sectionMetaMap = new Map();
            (preparedSections ?? []).forEach(section => {
                const key = normalizeSectionKey(section.configKey ?? section.originalHeading ?? section.heading);
                if (key) {
                    sectionMetaMap.set(key, section);
                }
            });
            const segmentConfig = resolvedSectionConfig.segment_title ?? frame.referenceParameters?.sections?.segment_title ?? {};
            const segmentVisible = segmentConfig.show !== false;
            const verticalLayoutEnabled = resolvedSectionConfig.vertical_sections ?? frame.referenceParameters?.sections?.vertical_sections;

            if (sections.length > 0) {
                return (
                    <div className="px-2 py-1 flex flex-col space-y-4 text-gray-500 @container">
                        <Checkbox
                            size="sm"
                            isSelected={segmentVisible}
                            onValueChange={() => { frame.setField(`referenceParameters.sections.segment_title.show`, !segmentVisible); }}
                        >Segment name</Checkbox>
                        <Checkbox
                            size="sm"
                            isSelected={verticalLayoutEnabled}
                            onValueChange={() => { frame.setField(`referenceParameters.sections.vertical_sections`, !verticalLayoutEnabled); }}
                        >Vertical layout</Checkbox>
                        {sections.map((sectionId) => {
                            const sectionMeta = sectionMetaMap.get(sectionId);
                            const sectionSettings = resolvedSectionConfig?.[sectionId]
                                ?? (sectionMeta?.heading ? resolvedSectionConfig?.[sectionMeta.heading] : undefined)
                                ?? {};
                            const sectionLabel = sectionMeta?.originalHeading ?? sectionId;

                            const show = sectionSettings.show !== false;
                            const includeHeading = sectionSettings.heading !== false;
                            const largeSpacing = sectionSettings.largeSpacing !== undefined
                                ? sectionSettings.largeSpacing !== false
                                : sectionMeta?.largeSpacing !== false;
                            const fontSize = sectionSettings.fontSize ?? sectionMeta?.fontSize ?? 16;
                            const fontStyle = sectionSettings.fontStyle ?? sectionMeta?.fontStyle ?? "normal";
                            const sectionStyle = sectionSettings.sectionStyle ?? sectionMeta?.sectionStyle ?? sectionMeta?.autoSectionStyle ?? "paragraphFallback";
                            const itemCount = sectionSettings.itemCount ?? sectionMeta?.itemCount;
                            const includeIcons = sectionSettings.includeIcons ?? sectionMeta?.includeIcons ?? false;
                            const showIconToggle = sectionStyle === "detailsBullets";
                            const headingText = sectionSettings.headingText ?? "";

                            return (
                                <div key={sectionId} className="rounded-md bg-background p-2 my-1 space-y-2">
                                    <Checkbox
                                        isSelected={show}
                                        size="sm"
                                        onValueChange={() => { frame.setField(`referenceParameters.sections.${sectionId}.show`, !show); }}
                                    >{sectionLabel}</Checkbox>
                                    {show && (
                                        <div className="flex flex-col space-y-2 ml-6 my-2">
                                            <div className="flex space-x-2 w-full">
                                                <Checkbox
                                                    isSelected={includeHeading}
                                                    size="sm"
                                                    onValueChange={() => { frame.setField(`referenceParameters.sections.${sectionId}.heading`, !includeHeading); }}
                                                >Include heading</Checkbox>
                                                <Checkbox
                                                    isSelected={largeSpacing}
                                                    size="sm"
                                                    onValueChange={() => { frame.setField(`referenceParameters.sections.${sectionId}.largeSpacing`, !largeSpacing); }}
                                                >Add spacing before section</Checkbox>
                                            </div>
                                            <div className="flex space-x-2 w-full ">
                                                <DebouncedNumberInput
                                                    value={fontSize}
                                                    variant="bordered"
                                                    size="sm"
                                                    label="Font size"
                                                    minValue={1}
                                                    maxValue={80}
                                                    onValueChange={(value) => {
                                                        let size
                                                        if (isNaN(value)) {
                                                            size = null
                                                        } else {
                                                            size = Math.min(Math.max(1, value), 144)
                                                            frame.setField(`referenceParameters.sections.${sectionId}.fontSize`, size)
                                                        }
                                                        return true
                                                    }}
                                                />
                                                <Select
                                                    className="w-full"
                                                    label="Font style"
                                                    size="sm"
                                                    variant="bordered"
                                                    selectedKeys={[fontStyle]}
                                                    disallowEmptySelection={true}
                                                    onSelectionChange={(v) => {
                                                        frame.setField(`referenceParameters.sections.${sectionId}.fontStyle`, Array.from(v)[0])
                                                    }}>
                                                    <SelectItem key="light">Light</SelectItem>
                                                    <SelectItem key="normal">Normal</SelectItem>
                                                    <SelectItem key="bold">Bold</SelectItem>
                                                    <SelectItem key="italic">Italic</SelectItem>
                                                </Select>
                                            </div>
                                            <DebouncedInput
                                                value={headingText}
                                                label="Heading override"
                                                size="sm"
                                                variant="bordered"
                                                placeholder={sectionMeta?.heading ?? sectionId}
                                                onChange={(value) => {
                                                    const trimmed = typeof value === "string" ? value.trim() : ""
                                                    frame.setField(
                                                        `referenceParameters.sections.${sectionId}.headingText`,
                                                        trimmed.length > 0 ? trimmed : null
                                                    )
                                                }}
                                            />
                                            <>
                                                <Select
                                                    className="w-full"
                                                    label="Render mode"
                                                    size="sm"
                                                    variant="bordered"
                                                    selectedKeys={[sectionStyle]}
                                                    disallowEmptySelection={true}
                                                    onSelectionChange={(v) => {
                                                        frame.setField(`referenceParameters.sections.${sectionId}.sectionStyle`, Array.from(v)[0])
                                                    }}>
                                                    <SelectItem key="paragraphFallback">Normal</SelectItem>
                                                    <SelectItem key="title">Title</SelectItem>
                                                    <SelectItem key="summary">Main summary</SelectItem>
                                                    <SelectItem key="detailsBullets">Bullets</SelectItem>
                                                    <SelectItem key="quotes">Quotes</SelectItem>
                                                    <SelectItem key="sentiment">Sentiment</SelectItem>
                                                    <SelectItem key="company_logos">Companies</SelectItem>
                                                    <SelectItem key="organizations">Organizations</SelectItem>
                                                </Select>
                                                <div className="flex items-end space-x-2">
                                                    <DebouncedNumberInput
                                                        value={itemCount ?? undefined}
                                                        variant="bordered"
                                                        size="sm"
                                                        label="Limit items"
                                                        minValue={1}
                                                        maxValue={100}
                                                        onValueChange={(value) => {
                                                            let size
                                                            if (isNaN(value)) {
                                                                size = null
                                                            } else {
                                                                size = Math.min(Math.max(1, value), 500)
                                                                frame.setField(`referenceParameters.sections.${sectionId}.itemCount`, size)
                                                            }
                                                            return true
                                                        }}
                                                    />
                                                    {showIconToggle && (
                                                        <Checkbox
                                                            isSelected={includeIcons}
                                                            size="sm"
                                                            onValueChange={() => {
                                                                frame.setField(`referenceParameters.sections.${sectionId}.includeIcons`, !includeIcons)
                                                            }}
                                                        >
                                                            Include icons
                                                        </Checkbox>
                                                    )}
                                                </div>
                                            </>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            }
            return (
                <div className="px-2 py-2 text-sm text-gray-500">
                    <p>No structured sections detected. Run the summary to populate sections.</p>
                </div>
            );
        }

                                        })()}
                                    </Panel>
                                </div>
                            </div>
                            <div className="text-xs px-3 pb-3 text-gray-600">#{frame.plainId}</div>
                    </Panel>
                </div>}

                <div className="space-y-2">
                    {!isProxyView && (["query", "view", "flow"].includes(frame.type) || (frame.type === "element" && frame.getConfig.extract === "items")) && viewConfigPanel()}
                    {!isProxyView && ["query", "view"].includes(frame.type) && categoryPanel()}
                    {!isProxyView && ["query", "view"].includes(frame.type) && filterAndAllocationsPanels()}
                    {!isProxyView && ["view", "flow"].includes(frame.type) && extensionsPanel()}
                </div>
                {frame.type === "flow" && flowPanel()}

                {!filters && (frame.type === "view" || frame.type === "action" || frame.type === "summary" || frame.type === "categorizer" || frame.type == "actionrunner") &&
                    <div className="space-y-2">
                        <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`${frame.metadata.title} details`}>
                            <div className="p-2 space-y-3">
                                {frame.type === "view" && (
                                    <Switch
                                        size="sm"
                                        isSelected={Boolean(frame.referenceParameters?.proxy)}
                                        onValueChange={handleProxyToggle}
                                    >
                                        Proxy anchored view
                                    </Switch>
                                )}
                                {isProxyView && frame.type === "view" ? (
                                    <div className="space-y-2">
                                        <p className="text-xs font-medium text-gray-500">Title</p>
                                        <DebouncedInput
                                            size="sm"
                                            variant="bordered"
                                            value={frame.title}
                                            placeholder="Title"
                                            className="w-full"
                                            onChange={(value) => {
                                                if (!value) {
                                                    return
                                                }
                                                frame.title = value
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <PrimitiveCard.Parameters
                                          primitive={frame}
                                          includeTitle
                                          editing
                                          leftAlign
                                          density="sm"
                                          className="text-xs text-slate-500"
                                          fullList
                                        />
                                        {frame.type === "actionrunner" && (
                                            <ActionFieldsEditor
                                                action={actionRunnerContext.activeAction}
                                                primitive={frame}
                                                className="mt-3"
                                                showLabel
                                                density="sm"
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Panel>
                        <Button fullWidth variant="bordered" onPress={() => mainstore.doPrimitiveAction(frame, frame.metadata.actions[0]?.key)}>Run</Button>
                    </div>
                }
                {frame.type === "page" && (
                    <div className="space-y-2">
                        <div className="border rounded-md bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 p-3">
                            <Select
                                className="w-full"
                                label="Theme"
                                size="sm"
                                variant="bordered"
                                selectedKeys={[frame.renderConfig?.theme ?? "default"]}
                                onSelectionChange={(v) => {
                                    frame.setField('renderConfig.theme', Array.from(v)[0]);
                                    updateFrame();
                                }}
                            >
                                {themeOptions.map(opt => (
                                    <SelectItem key={opt.key}>{opt.label}</SelectItem>
                                ))}
                            </Select>
                        </div>
                        <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`${frame.metadata.title} details`}>
                            <div className="px-4 pb-2 space-y-2 text-sm text-gray-600">
                                {pinSet("Inputs", "inputPins")}
                                {pinSet("Outputs", "outputPins")}
                            </div>
                            <div className="p-2 ">
                                <PrimitiveCard.Parameters
                                  primitive={frame}
                                  includeTitle
                                  editing
                                  leftAlign
                                  density="sm"
                                  className="text-xs text-slate-500"
                                  fullList
                                  showExtra
                                />
                            </div>
                        </Panel>
                    </div>
                )
                }

                {frame.type === "query" &&
                    <div className="space-y-2">
                        <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`${frame.metadata.title} details`}>
                            <div className="flex items-center justify-end gap-2 px-2 pt-2">
                                <AIProcessButton active='data_query' actionKey='custom_query' primitive={frame} />
                                {list.length > 0 && (
                                    <button
                                        type="button"
                                        className="flex font-medium grow-0 bg-white dark:bg-slate-950 hover:bg-gray-100 hover:shadow-sm hover:text-gray-600 justify-center p-1 rounded-full shrink-0 text-xs w-5 text-gray-400"
                                        onClick={(e) => clearItems(e)}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                                    </button>
                                )}
                            </div>
                            {frame.flowElement && (
                                <div className="px-2 pt-2">
                                    <Switch
                                        size="sm"
                                        isSelected={Boolean(frame.referenceParameters?.setTitleToFlow)}
                                        onValueChange={(value)=>frame.setField("referenceParameters.setTitleToFlow", value ? true : null)}
                                    >
                                        Set title to flow
                                    </Switch>
                                </div>
                            )}
                            <div className="p-2 ">
                                <PrimitiveCard.Parameters
                                  primitive={frame}
                                  includeTitle
                                  editing
                                  leftAlign
                                  density="sm"
                                  className="text-xs text-slate-500"
                                  fullList
                                  showExtra
                                />
                            </div>
                        </Panel>
                    </div>
                }
                {frame.type === "question" &&
                    <div className="space-y-2">
                        <Panel collapsable noSpace className="border rounded-md bg-gray-50 dark:bg-slate-900 px-3 py-2" title={`Questions`}>
                            <QuestionEditor primitive={frame} />
                            <div className="pt-2 px-2">
                                <PrimitiveCard.Parameters
                                  primitive={frame}
                                  includeTitle
                                  editing
                                  leftAlign
                                  density="sm"
                                  className="text-xs text-slate-500"
                                  fullList
                                  showExtra
                                />
                            </div>
                        </Panel>
                    </div>
                }
                {flowElementControl()}
                {underlyingInfo}
                {itemCategory && <div className="px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-md">
                    <CategoryHeader newPrimitiveCallback={newPrimitiveCallback} itemCategory={itemCategory} items={list} newItemParent={newItemParent} createNewView={props.createNewView} panelConfig={frame?.referenceParameters?.table} actionAnchor={frame} filters={filters} />
                </div>}
                {renderDescendantPanels(list, {
                    newPrimitiveCallback,
                    newItemParent,
                    actionAnchor: frame,
                    panelConfig: frame?.referenceParameters?.table,
                    createNewView: props.createNewView,
                    filters
                })}
                <div className="text-gray-500 text-sm">
                    <Panel collapsable noSpace narrow title="Ancestors">
                        {() => {
                            const [axisForPivot, parents] = getAncestors()

                            return <div className="space-y-2 mt-2">
                                <div className="border rounded-md p-2 space-y-2">
                                    {axisForPivot.map((d, i) => (
                                        <div key={d.category.id} className="p-2 bg-gray-50 dark:bg-slate-900 text-sm border border-gray-200 dark:border-slate-700 rounded-md ">
                                            <CategoryHeader itemCategory={d.category} newPrimitiveCallback={newPrimitiveCallback} newItemParent={newItemParent} pivotRelationship={d.relationship} createNewView={props.createNewView} panelConfig={frame?.referenceParameters?.table} actionAnchor={frame} items={parents.filter(d2 => d2.referenceId === d.category.id)} filters={filters} />
                                        </div>))}
                                </div>
                            </div>
                        }}
                    </Panel>
                </div>
            </div>
            <div className="mt-2">
                <nav aria-label="Tabs" className="border-t isolate flex divide-x divide-gray-200 shadow">
                    {mainTabs.map((tab, tabIdx) => (
                        <a
                            key={tab.name}
                            onClick={() => setActiveTab(tab)}
                            aria-current={tab.current ? 'page' : undefined}
                            className={classNames(
                                activeTab?.name === tab.name ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
                                'group relative min-w-0 flex-1 overflow-hidden bg-white dark:bg-slate-950 px-4 py-4 text-center text-sm font-medium hover:bg-gray-50 dark:bg-slate-900 focus:z-10',
                            )}
                        >
                            <span>{tab.name}</span>
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    activeTab?.name === tab.name ? 'bg-green-600' : 'bg-transparent',
                                    'absolute inset-x-0 bottom-0 h-0.5',
                                )}
                            />
                        </a>
                    ))}
                </nav>
                {activeTab?.referenceId && <div className="w-full flex flex-col p-3">
                    <NewPrimitivePanel key={activeTab.referenceId} newPrimitiveCallback={newPrimitiveCallback} parent={newItemParent} primitiveList={list} selectedCategory={mainstore.category(activeTab.referenceId)} />
                </div>}
            </div>
        </>
    }
    return <div className="bg-white dark:bg-slate-950 min-h-full">{content}</div>
}
